import { api } from "./api";
import {
  IAppointment,
  IAppointmentsProgram,
  IAppointmentsProgramsMetaData,
  ICompanyGroupsRRM,
  ICompanyGroupsRRMHeaders,
  IQuestion,
  IQuestionnaireCompanies,
  IQuestionnaireCompaniesHeaders,
  IQuestionnaireCompaniesTypes,
  IQuestionnaireData,
  TAppointmentsProgramsData,
  TAppointmentsProgramsScheduleResponse,
  TAppointmentsProgramsTherapist,
  TClinicSummaryResponce,
  TCurrency,
  TDataType,
  TFeature,
  TFeatures2Plans,
  TGeneralRRM,
  TGetStatusesResponse,
  TGetPendingAppointmentsProgramsResponse,
  TGroup,
  TNamedItem,
  TPlan,
  TPlanPrice,
  TQuestionOption,
  TQuestionsSpecificTypes,
  TQuestionsUsersFields,
  TCity,
  TSmsMessage,
  TSupportMessage,
  TSupportMessageComment,
  TCreateSupportTicketResponse,
  TUploadResponse,
  TVideoDiagnostics,
  IPayment,
  ITransaction,
  IPaymentResponse,
  IBalanceHistoryItem,
  ITogglePaymentStatusResponse,
  IBulkRefundRequest,
  IBulkRefundResponse,
  ICardLast4DigitsResponse,
  ICreatePaymentRequest,
  ICreatePaymentPageRequest,
  ICreatePaymentResponse,
  ICreateBulkPaymentJobRequest,
  ICreateBulkPaymentJobResponse,
  IJobDetail,
  IJobItem,
  IJobItemAttempt,
  IJobItemsRequest,
  IJobListItem,
  IJobListRequest,
  IJobRetryResponse,
  ITransactionRefundRequest,
  ITransactionRefundResponse,
  TRootCompanyOption,
  IAdminMessage,
  IDuplicateQuestionResponse,
  ICompanyDetails,
  ITreatmentRoom,
  ITreatmentGroup,
  ITreatmentGroupMember,
  ICreateGroupTreatmentPlanRequest,
  ICreateGroupTreatmentPlanResponse,
  ICreateAppointmentFastRequest,
  IProgramSessionsResponse,
  IProgramConfig,
  IConvertToSeriesRequest,
  IPaymentClearingCompany,
  ICompanyPaymentClearingConfiguration,
  IPaymentLinkSettings,
  IPaymentLinkSessionAdminList,
  IGetPaymentLinkSessionsRequest,
  ITreatmentDebtItem,
  IPaymentType,
  ICancelAppReason,
  ICancelAppRequest,
  IPaymentCollectionMethod,
  PaymentCollectionMethodsResponse,
  IAppTable,
  ITablePermission,
  IUserAppTableRule,
  ICompanyUserEnterQuestionnaire,
  IQuestionnaireCompaniesSpecificTypes,
  TSMSDeliveryType,
  TUpsertItem,
} from "../types/utils.types";
import { IFinancialDashboardResponse } from "../types/financialDashboard.types";
import { TExercise, TExercisesCategory } from "../../utils/constants";
import { TCompany } from "../types/users.types";
import {
  onBoardingPaymentRequest,
  IPaymentLinkResponse,
  IChangeSubscriptionPlanRequest,
  IChangeSubscriptionCardRequest,
} from "../types/onboardint.types";
import { number } from "yup";

const pick = <T = any>(value: any, pascalKey: string, camelKey: string, fallback?: T): T => {
  const picked = value?.[pascalKey] ?? value?.[camelKey];
  return (picked ?? fallback) as T;
};

const normalizeJobItem = (item: any): IJobItem => ({
  id: pick<number>(item, "Id", "id", 0),
  jobId: pick<number>(item, "JobId", "jobId", 0),
  itemType: pick<string>(item, "ItemType", "itemType", ""),
  itemKey: pick<string>(item, "ItemKey", "itemKey", ""),
  referenceId: pick<number | null>(item, "ReferenceId", "referenceId", null),
  referenceGuid: pick<string | null>(item, "ReferenceGuid", "referenceGuid", null),
  displayName: pick<string | null>(item, "DisplayName", "displayName", null),
  status: pick<string>(item, "Status", "status", ""),
  isRetryable: pick<boolean>(item, "IsRetryable", "isRetryable", false),
  retryCount: pick<number>(item, "RetryCount", "retryCount", 0),
  lastErrorClassification: pick<string | null>(item, "LastErrorClassification", "lastErrorClassification", null),
  lastErrorCode: pick<string | null>(item, "LastErrorCode", "lastErrorCode", null),
  lastErrorMessage: pick<string | null>(item, "LastErrorMessage", "lastErrorMessage", null),
  resultJson: pick<string | null>(item, "ResultJson", "resultJson", null),
  lastHangfireJobId: pick<string | null>(item, "LastHangfireJobId", "lastHangfireJobId", null),
  createdAtUtc: pick<string>(item, "CreatedAtUtc", "createdAtUtc", ""),
  updatedAtUtc: pick<string>(item, "UpdatedAtUtc", "updatedAtUtc", ""),
  lastAttemptAtUtc: pick<string | null>(item, "LastAttemptAtUtc", "lastAttemptAtUtc", null),
  completedAtUtc: pick<string | null>(item, "CompletedAtUtc", "completedAtUtc", null),
});

const normalizeJob = (job: any): IJobListItem => {
  const permanentFailedItems = pick<number>(job, "PermanentFailedItems", "permanentFailedItems", 0);
  const systemFailedItems = pick<number>(job, "SystemFailedItems", "systemFailedItems", 0);

  return {
    id: pick<number>(job, "Id", "id", 0),
    publicId: pick<string>(job, "PublicId", "publicId", ""),
    jobType: pick<string>(job, "JobType", "jobType", ""),
    jobSubType: pick<string | null>(job, "JobSubType", "jobSubType", null),
    status: pick<string>(job, "Status", "status", ""),
    companyId: pick<number>(job, "CompanyId", "companyId", 0),
    fakeCompanyId: pick<number | null>(job, "FakeCompanyId", "fakeCompanyId", null),
    createdByUserId: pick<number>(job, "CreatedByUserId", "createdByUserId", 0),
    totalItems: pick<number>(job, "TotalItems", "totalItems", 0),
    succeededItems: pick<number>(job, "SucceededItems", "succeededItems", 0),
    skippedItems: pick<number>(job, "SkippedItems", "skippedItems", 0),
    permanentFailedItems,
    systemFailedItems,
    failedItems: pick<number>(job, "FailedItems", "failedItems", permanentFailedItems + systemFailedItems),
    createdAtUtc: pick<string>(job, "CreatedAtUtc", "createdAtUtc", ""),
    updatedAtUtc: pick<string>(job, "UpdatedAtUtc", "updatedAtUtc", ""),
    startedAtUtc: pick<string | null>(job, "StartedAtUtc", "startedAtUtc", null),
    completedAtUtc: pick<string | null>(job, "CompletedAtUtc", "completedAtUtc", null),
    source: pick<string | null>(job, "Source", "source", null),
    notes: pick<string | null>(job, "Notes", "notes", null),
    parametersJson: pick<string | null>(job, "ParametersJson", "parametersJson", null),
  };
};

const normalizeJobDetail = (job: any): IJobDetail => ({
  ...normalizeJob(job),
  items: (pick<any[]>(job, "Items", "items", []) || []).map(normalizeJobItem),
});

const normalizeJobAttempt = (attempt: any): IJobItemAttempt => ({
  id: pick<number>(attempt, "Id", "id", 0),
  jobItemId: pick<number>(attempt, "JobItemId", "jobItemId", 0),
  attemptNumber: pick<number>(attempt, "AttemptNumber", "attemptNumber", 0),
  status: pick<string>(attempt, "Status", "status", ""),
  errorClassification: pick<string>(attempt, "ErrorClassification", "errorClassification", ""),
  errorCode: pick<string | null>(attempt, "ErrorCode", "errorCode", null),
  errorMessage: pick<string | null>(attempt, "ErrorMessage", "errorMessage", null),
  hangfireJobId: pick<string | null>(attempt, "HangfireJobId", "hangfireJobId", null),
  requestedByUserId: pick<number | null>(attempt, "RequestedByUserId", "requestedByUserId", null),
  createdAtUtc: pick<string>(attempt, "CreatedAtUtc", "createdAtUtc", ""),
  startedAtUtc: pick<string | null>(attempt, "StartedAtUtc", "startedAtUtc", null),
  completedAtUtc: pick<string | null>(attempt, "CompletedAtUtc", "completedAtUtc", null),
});

const normalizeJobRetryResponse = (response: any): IJobRetryResponse => ({
  success: pick<boolean>(response, "Success", "success", false),
  status: pick<string>(response, "Status", "status", ""),
  requestedItemCount: pick<number>(response, "RequestedItemCount", "requestedItemCount", 0),
  enqueuedItemCount: pick<number>(response, "EnqueuedItemCount", "enqueuedItemCount", 0),
  message: pick<string | null>(response, "Message", "message", null),
  hangfireJobId: pick<string | null>(response, "HangfireJobId", "hangfireJobId", null),
});

const normalizeBulkPaymentJobResponse = (response: any): ICreateBulkPaymentJobResponse => ({
  success: pick<boolean>(response, "Success", "success", false),
  jobId: pick<number>(response, "JobId", "jobId", 0),
  jobPublicId: pick<string>(response, "JobPublicId", "jobPublicId", ""),
  jobType: pick<string>(response, "JobType", "jobType", ""),
  status: pick<string>(response, "Status", "status", ""),
  totalItems: pick<number>(response, "TotalItems", "totalItems", 0),
  message: pick<string | null>(response, "Message", "message", null),
});

function readIsRapidInvoiceRecover(obj: unknown): boolean | undefined {
  if (obj == null || typeof obj !== "object") return undefined;
  const record = obj as Record<string, unknown>;
  const v = record.isRapidInvoiceRecover ?? record.IsRapidInvoiceRecover;
  if (v === true || v === "true") return true;
  if (v === false || v === "false") return false;
  return undefined;
}

function normalizePaymentForRapidInvoice(ip: IPayment): IPayment {
  const paymentFlag = readIsRapidInvoiceRecover(ip);
  const transactions: ITransaction[] = (ip.transactions ?? []).map((tx) => {
    const txFlag = readIsRapidInvoiceRecover(tx);
    const merged =
      txFlag !== undefined ? txFlag : paymentFlag !== undefined ? paymentFlag : undefined;
    if (merged === undefined) return tx;
    return { ...tx, isRapidInvoiceRecover: merged };
  });
  const out: IPayment = { ...ip, transactions };
  if (paymentFlag !== undefined) out.isRapidInvoiceRecover = paymentFlag;
  return out;
}

function normalizePaymentsApiResponse(response: unknown): IPaymentResponse {
  const res = response as IPaymentResponse;
  if (!res?.data || !Array.isArray(res.data)) return res;
  return {
    ...res,
    data: res.data.map((p) => normalizePaymentForRapidInvoice(p)),
  };
}

export const utilsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getSupportMessages: build.mutation<TSupportMessage[], void>({
      query: () => ({
        url: `Admintech/Admin_Supports_DML/1/`,
        method: "POST",
        body: { body: "", subject: "" },
      }),
    }),
    postSupportMessage: build.mutation<
      TSupportMessage,
      Partial<TSupportMessage>
    >({
      query: (body) => ({
        url: `Admintech/Admin_Supports_DML/2/`,
        method: "POST",
        body,
      }),
    }),
    createSupportTicket: build.mutation<
      TCreateSupportTicketResponse,
      { subject: string; body: string; screenType: number }
    >({
      query: (body) => ({
        url: `Admintech/Supports/CreateTicket`,
        method: "POST",
        body,
      }),
    }),
    postSupportMessageComment: build.mutation<
      TSupportMessageComment,
      Partial<TSupportMessageComment>
    >({
      query: (body) => ({
        url: `Admintech/PostCommentsToSupportMessage/2/`,
        method: "POST",
        body,
      }),
    }),
    postCommentsToAppointmentsPrograms: build.mutation<
      any,
      {
        AppointmentsProgramsId: number;
        UsersGuid: string;
        TherapistsGuid: string;
        Comment: string;
      }
    >({
      query: (body) => ({
        url: `therapists/Therapist_PostCommentsToAppointmentsPrograms`,
        method: "POST",
        body,
      }),
    }),
    getAppointmentsProgramsComments: build.query<
      { comment: string; createOn: string; therapistName: string }[],
      number
    >({
      query: (appointmentsProgramsId) => ({
        url: `therapists/GetAppointmentsProgramsComments/${appointmentsProgramsId}`,
        method: "GET",
      }),
    }),
    //2 support file, 3 audio file for ai
    postFile: build.mutation<
      TUploadResponse,
      {
        file: File;
        usersId: string | number;
        supportId?: string | number;
        type?: number;
      }
    >({
      query: ({ file, usersId, supportId, type = 2 }) => {
        const formData = new FormData();
        let fileToUpload = file;
        if (supportId) {
          const extension = file.name.split(".").pop();
          fileToUpload = new File([file], `${supportId}.${extension}`, {
            type: file.type,
          });
        }

        formData.append("file", fileToUpload);
        return {
          url: `files/UploadFiles/${type}/${usersId}`,
          method: "POST",
          body: formData,
        };
      },
    }),
    postVideo: build.mutation<
      TVideoDiagnostics,
      {
        file: File;
        type: number;
        usersId: string | number;
        lang?: string;
      }
    >({
      query: ({ file, usersId, type = 5, lang = "he" }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `AI/getVideoDiagnostics/${type}/${0}/mp4/${lang}`,
          method: "POST",
          body: formData,
        };
      },
    }),

    getSupportStatuses: build.query<TGetStatusesResponse, void>({
      query: () => ({
        url: `/General/Gen_GetTableByTableName/StatusSupports`,
        method: "GET",
      }),
    }),
    getMigzars: build.query<TNamedItem[], void>({
      query: () => ({
        url: `/General/Gen_GetTableByTableName/migzars`,
        method: "GET",
      }),
    }),
    getGenders: build.query<TNamedItem[], void>({
      query: () => ({
        url: `/General/Gen_GetTableByTableName/genders`,
        method: "GET",
      }),
    }),
    getTherapyTypes: build.query<TNamedItem[], void>({
      query: () => ({
        url: `Admintech/Admin_TherapyTypes_DML/1`,
        method: "POST",
        body: { Name: "1" },
      }),
    }),
    getRoomsFeatures: build.query<TNamedItem[], void>({
      query: () => ({
        url: `Admintech/Admin_RoomsFeatures_DML/1`,
        method: "POST",
        body: { Name: "1" },
      }),
    }),
    getCompanies: build.mutation<TCompany[], void>({
      query: () => ({
        url: `Admintech/Admin_Companies_DML/1`,
        method: "POST",
        body: {},
      }),
    }),
    getCompanyById: build.query<ICompanyDetails, number>({
      query: (id) => ({
        url: `Companies/GetCompanyById/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Company", id }],
    }),
    setAudioRetention: build.mutation<
      { success: boolean; companyId: number; audioRetentionDays: number | null; message: string },
      { companyId: number; retentionDays: number | null }
    >({
      query: ({ companyId, retentionDays }) => ({
        url: `Companies/SetAudioRetention/${companyId}`,
        method: "PUT",
        body: { retentionDays },
      }),
      invalidatesTags: (result, error, { companyId }) => [
        { type: "Company", id: companyId },
      ],
    }),
    getAudioRetention: build.query<
      { success: boolean; companyId: number; audioRetentionDays: number | null; effectiveRetentionDays: number | null; inheritedFromCompanyId: number | null },
      number
    >({
      query: (companyId) => ({
        url: `Companies/GetAudioRetention/${companyId}`,
        method: "GET",
      }),
      providesTags: (result, error, companyId) => [{ type: "Company", id: companyId }],
    }),
    // Payment Clearing Configuration endpoints (Super Admin only)
    getPaymentClearingCompanies: build.query<IPaymentClearingCompany[], void>({
      query: () => ({
        url: `Admintech/GetPaymentClearingCompanies`,
        method: "GET",
      }),
      transformResponse: (
        response: Array<{
          Id?: number;
          Name?: string;
          id?: number;
          name?: string;
        }>
      ) =>
        (response || []).map((pc) => ({
          id: pc.id ?? pc.Id ?? 0,
          name: pc.name ?? pc.Name ?? "",
        })),
    }),
    getCompanyPaymentClearingConfiguration: build.query<
      ICompanyPaymentClearingConfiguration,
      number
    >({
      query: (companyId) => ({
        url: `Admintech/GetCompanyPaymentClearingConfiguration/${companyId}`,
        method: "GET",
      }),
      transformResponse: (response: {
        Id?: number;
        CompanyId?: number;
        BaseUrl?: string;
        APIKey?: string;
        SecretKey?: string;
        TerminalUID?: string;
        CushierUID?: string;
        PaymentPageUid?: string;
        PaymentClearingCompaniesId?: number;
        StatusId?: number;
        // Also handle camelCase in case backend changes
        id?: number;
        companyId?: number;
        baseUrl?: string;
        apiKey?: string;
        secretKey?: string;
        terminalUID?: string;
        cushierUID?: string;
        paymentPageUid?: string;
        paymentClearingCompaniesId?: number;
        statusId?: number;
      }) => ({
        id: response.id ?? response.Id,
        companyId: response.companyId ?? response.CompanyId,
        baseUrl: response.baseUrl ?? response.BaseUrl,
        apiKey: response.apiKey ?? response.APIKey,
        secretKey: response.secretKey ?? response.SecretKey,
        terminalUID: response.terminalUID ?? response.TerminalUID,
        cushierUID: response.cushierUID ?? response.CushierUID,
        paymentPageUid: response.paymentPageUid ?? response.PaymentPageUid,
        paymentClearingCompaniesId:
          response.paymentClearingCompaniesId ??
          response.PaymentClearingCompaniesId,
        statusId: response.statusId ?? response.StatusId ?? 1,
      }),
      providesTags: (result, error, companyId) => [
        { type: "PaymentClearingConfig" as const, id: companyId },
      ],
    }),
    saveCompanyPaymentClearingConfiguration: build.mutation<
      ICompanyPaymentClearingConfiguration,
      ICompanyPaymentClearingConfiguration
    >({
      query: (config) => ({
        url: `Admintech/SaveCompanyPaymentClearingConfiguration`,
        method: "POST",
        body: config,
      }),
      invalidatesTags: (result, error, config) => [
        { type: "PaymentClearingConfig" as const, id: config.companyId },
      ],
    }),
    getPaymentLinkSettings: build.query<
      IPaymentLinkSettings,
      { companyId: number; fakeCompanyId?: number | null }
    >({
      query: ({ companyId, fakeCompanyId }) => ({
        url: `Admintech/GetPaymentLinkSettings`,
        method: "GET",
        params: {
          companyId,
          ...(fakeCompanyId ? { fakeCompanyId } : {}),
        },
      }),
      transformResponse: (response: any): IPaymentLinkSettings => ({
        id: pick<number | null>(response, "Id", "id", null),
        companyId: pick<number | null>(response, "CompanyId", "companyId", null),
        fakeCompanyId: pick<number | null>(response, "FakeCompanyId", "fakeCompanyId", null),
        ttlMinutes: pick<number>(response, "TtlMinutes", "ttlMinutes", 1440),
        statusId: pick<number>(response, "StatusId", "statusId", 1),
      }),
      providesTags: (result, error, args) => [
        { type: "PaymentLinkSettings" as const, id: `${args.companyId}:${args.fakeCompanyId ?? "company"}` },
      ],
    }),
    savePaymentLinkSettings: build.mutation<
      IPaymentLinkSettings,
      IPaymentLinkSettings
    >({
      query: (settings) => ({
        url: `Admintech/SavePaymentLinkSettings`,
        method: "POST",
        body: settings,
      }),
      invalidatesTags: (result, error, settings) => [
        { type: "PaymentLinkSettings" as const, id: `${settings.companyId}:${settings.fakeCompanyId ?? "company"}` },
        { type: "PaymentLinkSessions" as const, id: `company:${settings.companyId}` },
      ],
    }),
    getPaymentLinkSessions: build.query<
      IPaymentLinkSessionAdminList,
      IGetPaymentLinkSessionsRequest
    >({
      query: (request) => ({
        url: `Admintech/GetPaymentLinkSessions`,
        method: "GET",
        params: {
          companyId: request.companyId,
          page: request.page ?? 1,
          pageSize: request.pageSize ?? 10,
          ...(request.fakeCompanyId ? { fakeCompanyId: request.fakeCompanyId } : {}),
          ...(request.clientUserId ? { clientUserId: request.clientUserId } : {}),
          ...(request.lowProfileId ? { lowProfileId: request.lowProfileId } : {}),
          ...(request.status ? { status: request.status } : {}),
        },
      }),
      transformResponse: (response: any): IPaymentLinkSessionAdminList => ({
        total: pick<number>(response, "Total", "total", 0),
        page: pick<number>(response, "Page", "page", 1),
        pageSize: pick<number>(response, "PageSize", "pageSize", 10),
        items: (pick<any[]>(response, "Items", "items", []) || []).map((item) => ({
          id: pick<string>(item, "Id", "id", ""),
          clientUserId: pick<number>(item, "ClientUserId", "clientUserId", 0),
          clientName: pick<string | null>(item, "ClientName", "clientName", null),
          companyId: pick<number>(item, "CompanyId", "companyId", 0),
          fakeCompanyId: pick<number | null>(item, "FakeCompanyId", "fakeCompanyId", null),
          operation: pick<string>(item, "Operation", "operation", ""),
          wrapperLinkDeliveryChannel: pick<number>(item, "WrapperLinkDeliveryChannel", "wrapperLinkDeliveryChannel", 0),
          expiresAtUtc: pick<string>(item, "ExpiresAtUtc", "expiresAtUtc", ""),
          latestLowProfileId: pick<string | null>(item, "LatestLowProfileId", "latestLowProfileId", null),
          latestCardComCreatedAtUtc: pick<string | null>(item, "LatestCardComCreatedAtUtc", "latestCardComCreatedAtUtc", null),
          latestCardComExpiresAtUtc: pick<string | null>(item, "LatestCardComExpiresAtUtc", "latestCardComExpiresAtUtc", null),
          status: pick<string>(item, "Status", "status", ""),
          createdAtUtc: pick<string>(item, "CreatedAtUtc", "createdAtUtc", ""),
          verifiedAtUtc: pick<string | null>(item, "VerifiedAtUtc", "verifiedAtUtc", null),
          redirectedAtUtc: pick<string | null>(item, "RedirectedAtUtc", "redirectedAtUtc", null),
          paidAtUtc: pick<string | null>(item, "PaidAtUtc", "paidAtUtc", null),
          lockedAtUtc: pick<string | null>(item, "LockedAtUtc", "lockedAtUtc", null),
          updatedAtUtc: pick<string | null>(item, "UpdatedAtUtc", "updatedAtUtc", null),
          auditEvents: (pick<any[]>(item, "AuditEvents", "auditEvents", []) || []).map((event) => ({
            id: pick<number>(event, "Id", "id", 0),
            eventType: pick<string>(event, "EventType", "eventType", ""),
            actorUserId: pick<number | null>(event, "ActorUserId", "actorUserId", null),
            lowProfileId: pick<string | null>(event, "LowProfileId", "lowProfileId", null),
            metadataJson: pick<string | null>(event, "MetadataJson", "metadataJson", null),
            createdAtUtc: pick<string>(event, "CreatedAtUtc", "createdAtUtc", ""),
          })),
        })),
      }),
      providesTags: (result, error, request) => [
        { type: "PaymentLinkSessions" as const, id: `company:${request.companyId}` },
      ],
    }),
    getRootCompanies: build.query<TRootCompanyOption[], number | undefined>({
      query: (companyId) => ({
        url: companyId
          ? `Admintech/Admin_Companies_GetRootCompanies/${companyId}`
          : `Admintech/Admin_Companies_GetRootCompanies`,
        method: "GET",
      }),
    }),
    getCompanyChargeTriggers: build.query<
      Array<{ id: number; name: string; nameEn: string }>,
      void
    >({
      query: () => ({
        url: `General/Gen_GetTableByTableName/CompanyChargeTriggers`,
        method: "GET",
      }),
      transformResponse: (
        response: Array<{
          Id?: number;
          Name?: string;
          NameEn?: string;
          StatusId?: number;
          id?: number;
          name?: string;
          nameEn?: string;
          statusId?: number;
        }>
      ) =>
        (response || [])
          .filter((ct) => (ct.statusId ?? ct.StatusId) === 1)
          .map((ct) => ({
            id: ct.id ?? ct.Id ?? 0,
            name: ct.name ?? ct.Name ?? "",
            nameEn: ct.nameEn ?? ct.NameEn ?? "",
          })),
    }),
    // Company Funding Sources endpoints
    getCompanyFundingSources: build.query<
      {
        CompanyFundingSourcesListResults: Array<{
          cfs: {
            id: number;
            companyId: number;
            fundingSourcesId: number;
            statusId: number;
          };
          fs: { id: number; name: string } | null;
        }>;
        FundingSourcesList: Array<{ id: number; name: string }>;
      },
      { companyId: number }
    >({
      query: ({ companyId }) => ({
        url: `Admintech/Admin_CompanyFundingSources_DML/1`,
        method: "POST",
        body: { companyId },
      }),
      transformResponse: (response: {
        CompanyFundingSourcesListResults?: Array<{
          cfs?: {
            Id?: number;
            CompanyId?: number;
            FundingSourcesId?: number;
            StatusId?: number;
            id?: number;
            companyId?: number;
            fundingSourcesId?: number;
            statusId?: number;
          };
          fs?: {
            Id?: number;
            Name?: string;
            id?: number;
            name?: string;
          } | null;
        }>;
        FundingSourcesList?: Array<{
          Id?: number;
          Name?: string;
          id?: number;
          name?: string;
        }>;
      }) => ({
        CompanyFundingSourcesListResults: (
          response.CompanyFundingSourcesListResults || []
        )
          .filter(
            (item) => (item.cfs?.statusId ?? item.cfs?.StatusId ?? 0) === 1
          )
          .map((item) => ({
            cfs: {
              id: item.cfs?.id ?? item.cfs?.Id ?? 0,
              companyId: item.cfs?.companyId ?? item.cfs?.CompanyId ?? 0,
              fundingSourcesId:
                item.cfs?.fundingSourcesId ?? item.cfs?.FundingSourcesId ?? 0,
              statusId: item.cfs?.statusId ?? item.cfs?.StatusId ?? 0,
            },
            fs: item.fs
              ? {
                  id: item.fs.id ?? item.fs.Id ?? 0,
                  name: item.fs.name ?? item.fs.Name ?? "",
                }
              : null,
          })),
        FundingSourcesList: (response.FundingSourcesList || []).map((fs) => ({
          id: fs.id ?? fs.Id ?? 0,
          name: fs.name ?? fs.Name ?? "",
        })),
      }),
      providesTags: ["CompanyFundingSources"],
    }),
    addCompanyFundingSource: build.mutation<
      unknown,
      { companyId: number; fundingSourcesId: number }
    >({
      query: (body) => ({
        url: `Admintech/Admin_CompanyFundingSources_DML/2`,
        method: "POST",
        body: { id: 0, ...body, statusId: 1 },
      }),
      invalidatesTags: ["CompanyFundingSources"],
    }),
    removeCompanyFundingSource: build.mutation<
      unknown,
      { id: number; companyId: number; fundingSourcesId: number }
    >({
      query: (body) => ({
        url: `Admintech/Admin_CompanyFundingSources_DML/2`,
        method: "POST",
        body: { ...body, statusId: 0 },
      }),
      invalidatesTags: ["CompanyFundingSources"],
    }),
    addOrEditCompany: build.mutation<TCompany[], Partial<TCompany>>({
      query: (company) => ({
        url: `Admintech/Admin_Companies_DML/2`,
        method: "POST",
        body: company,
      }),
      invalidatesTags: (result, error, company) => [
        { type: "Company", id: company.id },
      ],
    }),
    deleteCompany: build.mutation<TCompany[], TCompany>({
      //changes company status and all active users status to 0
      query: (company) => ({
        url: `Admintech/Admin_Companies_DML/3`,
        method: "POST",
        body: company,
      }),
    }),
    restoreCompany: build.mutation<TCompany[], TCompany>({
      //restores company and its users status
      query: (company) => ({
        url: `Admintech/Admin_Companies_DML/4`,
        method: "POST",
        body: company,
      }),
    }),
    changeTherapistsCanSeePrices: build.mutation<TCompany, { id: number; therapistsCanSeePrices: boolean }>({
      //changes therapists can see prices status
      query: (body) => ({
        url: `Admintech/Admin_Companies_DML/5`,
        method: "POST",
        body,
      }),
    }),
    changeCanAddDefaultReports: build.mutation<TCompany, { id: number; canAddDefaultReports: boolean }>({
      query: (body) => ({
        url: `Admintech/Admin_Companies_DML/6`,
        method: "POST",
        body,
      }),
    }),
    changeAdminCanViewPatientRecordings: build.mutation<TCompany, { id: number; adminCanViewPatientRecordings: boolean }>({
      query: (body) => ({
        url: `Admintech/Admin_Companies_DML/7`,
        method: "POST",
        body,
      }),
    }),
    getQuestionnaires: build.mutation<
      IQuestionnaireData[],
      void
    >({
      query: () => ({
        url: `Admintech/Admin_Questionnaire_DML/1`,
        method: "POST",
        body: { questionnaire: true },
      }),
    }),
    addOrEditQuestionnaire: build.mutation<
      IQuestionnaireData[],
      { body: Partial<IQuestionnaireData> }
    >({
      query: ({ body }) => ({
        url: `Admintech/Admin_Questionnaire_DML/2`,
        method: "POST",
        body,
      }),
    }),
    deleteQuestionnaire: build.mutation<
      IQuestionnaireData[],
      { questionnaire: IQuestionnaireData }
    >({
      query: ({ questionnaire }) => ({
        url: `Admintech/Admin_Questionnaire_DML/3`,
        method: "POST",
        body: questionnaire,
      }),
    }),
    copyQuestionnaireWithQuestionsAndOptions: build.mutation<
      IQuestionnaireData[],
      { questionnaire: Partial<IQuestionnaireData> }
    >({
      query: ({ questionnaire }) => ({
        url: `Admintech/Admin_Questionnaire_DML/4`,
        method: "POST",
        body: questionnaire,
      }),
    }),
    getQuestions: build.mutation<IQuestion[], { questionnaireId: string }>({
      query: ({ questionnaireId }) => ({
        url: `Admintech/Admin_Questions_DML/1/${questionnaireId}`,
        method: "POST",
        body: [],
      }),
    }),
    addOrEditQuestions: build.mutation<
      IQuestion[],
      { questionnaireId: string; questions: Partial<IQuestion>[] }
    >({
      query: ({ questionnaireId, questions }) => ({
        url: `Admintech/Admin_Questions_DML/2/${questionnaireId}`,
        method: "POST",
        body: questions,
      }),
    }),
    deleteQuestions: build.mutation<
      IQuestion[],
      { questionnaireId: string | number; question: IQuestion }
    >({
      query: ({ questionnaireId, question }) => ({
        url: `Admintech/Admin_Questions_DML/3/${questionnaireId}`,
        method: "POST",
        body: [question],
      }),
    }),
    deleteMultipleQuestions: build.mutation<
      IQuestion[],
      { questionnaireId: string | number; questions: IQuestion[] }
    >({
      query: ({ questionnaireId, questions }) => ({
        url: `Admintech/Admin_Questions_DML/3/${questionnaireId}`,
        method: "POST",
        body: questions,
      }),
    }),
    duplicateQuestion: build.mutation<
      IDuplicateQuestionResponse,
      { questionnaireId: string; sourceQuestionsId: number }
    >({
      query: ({ questionnaireId, sourceQuestionsId }) => ({
        url: `Admintech/Admin_DuplicateQuestion/${questionnaireId}/${sourceQuestionsId}`,
        method: "POST",
        body: [],
      }),
    }),
    getQuestionsOptions: build.mutation<
      TQuestionOption[],
      { questionsId: number | string }
    >({
      query: ({ questionsId }) => ({
        url: `Admintech/Admin_QuestionsOptions_DML/1/${questionsId}`,
        method: "POST",
        body: [],
      }),
    }),
    addOrEditQuestionsOptions: build.mutation<
      TQuestionOption[],
      { questionsId: number | string; options: TQuestionOption[] }
    >({
      query: ({ questionsId, options }) => ({
        url: `Admintech/Admin_QuestionsOptions_DML/2/${questionsId}`,
        method: "POST",
        body: options,
      }),
    }),
    deleteQuestionsOptions: build.mutation<
      TQuestionOption[],
      { questionsId: number | string; option: TQuestionOption }
    >({
      query: ({ questionsId, option }) => ({
        url: `Admintech/Admin_QuestionsOptions_DML/3/${questionsId}`,
        method: "POST",
        body: [option],
      }),
    }),
    getQuestionsGroups: build.mutation<
      TGroup[],
      { questionsId: number | string }
    >({
      query: ({ questionsId }) => ({
        url: `Admintech/Admin_Groups_DML/1/${questionsId}`,
        method: "POST",
        body: [],
      }),
    }),
    addOrEditQuestionsGroups: build.mutation<
      TGroup[],
      { questionsId: string | number; groups: TGroup[] }
    >({
      query: ({ questionsId, groups }) => ({
        url: `Admintech/Admin_Groups_DML/2/${questionsId}`,
        method: "POST",
        body: groups,
      }),
    }),
    deleteQuestionsGroups: build.mutation<
      TGroup[],
      { questionsId: string | number; group: TGroup }
    >({
      query: ({ questionsId, group }) => ({
        url: `Admintech/Admin_Groups_DML/3/${questionsId}`,
        method: "POST",
        body: [group],
      }),
    }),
    getCompanyGroupsRRMHeaders: build.mutation<
      ICompanyGroupsRRMHeaders[],
      { companyId: number }
    >({
      query: ({ companyId }) => ({
        url: `Admintech/Admin_CompanyGroupsRRMHeaders_DML/1/${companyId}`,
        method: "POST",
        body: {},
      }),
    }),
    addOrEditCompanyGroupsRRMHeaders: build.mutation<
      ICompanyGroupsRRMHeaders[],
      { companyId: string | number; RRMHeaders: ICompanyGroupsRRMHeaders }
    >({
      query: ({ companyId, RRMHeaders }) => ({
        url: `Admintech/Admin_CompanyGroupsRRMHeaders_DML/2/${companyId}`,
        method: "POST",
        body: RRMHeaders,
      }),
    }),
    deleteCompanyGroupsRRMHeaders: build.mutation<
      ICompanyGroupsRRMHeaders[],
      { companyId: string | number; RRMHeader: ICompanyGroupsRRMHeaders }
    >({
      query: ({ companyId, RRMHeader }) => ({
        url: `Admintech/Admin_CompanyGroupsRRMHeaders_DML/3/${companyId}`,
        method: "POST",
        body: RRMHeader,
      }),
    }),
    getCompanyGroupsRRM: build.mutation<
      ICompanyGroupsRRM[],
      { companyGroupsRRMHeadersId: number }
    >({
      query: ({ companyGroupsRRMHeadersId }) => ({
        url: `Admintech/Admin_CompanyGroupsRRM_DML/1/${companyGroupsRRMHeadersId}`,
        method: "POST",
        body: [{ id: companyGroupsRRMHeadersId }],
      }),
    }),
    addOrEditCompanyGroupsRRM: build.mutation<
      ICompanyGroupsRRM[],
      {
        companyGroupsRRMHeadersId: string | number;
        RRMGroup: ICompanyGroupsRRM[];
      }
    >({
      query: ({ companyGroupsRRMHeadersId, RRMGroup }) => ({
        url: `Admintech/Admin_CompanyGroupsRRM_DML/2/${companyGroupsRRMHeadersId}`,
        method: "POST",
        body: RRMGroup,
      }),
    }),
    deleteCompanyGroupsRRM: build.mutation<
      ICompanyGroupsRRM[],
      {
        companyGroupsRRMHeadersId: string | number;
        RRMGroupsId: string | number;
      }
    >({
      query: ({ companyGroupsRRMHeadersId, RRMGroupsId }) => ({
        url: `Admintech/Admin_CompanyGroupsRRM_DML/3/${companyGroupsRRMHeadersId}`,
        method: "POST",
        body: [{ id: RRMGroupsId }],
      }),
    }),
    getCompanyGeneralRRM: build.mutation<TGeneralRRM[], { companyId: number }>({
      query: ({ companyId }) => ({
        url: `Admintech/Admin_CompanyGeneralRRM_DML/1/${companyId}`,
        method: "POST",
        body: [{ id: companyId }],
      }),
    }),
    addOrEditCompanyGeneralRRM: build.mutation<
      TGeneralRRM[],
      {
        companyId: string | number;
        rrm: TGeneralRRM[];
      }
    >({
      query: ({ companyId, rrm }) => ({
        url: `Admintech/Admin_CompanyGeneralRRM_DML/2/${companyId}`,
        method: "POST",
        body: rrm,
      }),
    }),
    deleteCompanyGeneralRRM: build.mutation<
      TGeneralRRM[],
      {
        companyId: string | number;
        rrm: TGeneralRRM;
      }
    >({
      query: ({ companyId, rrm }) => ({
        url: `Admintech/Admin_CompanyGeneralRRM_DML/3/${companyId}`,
        method: "POST",
        body: [rrm],
      }),
    }),
    getAdminMessages: build.mutation<
      IAdminMessage[],
      { companyId: number; roleId?: number }
    >({
      query: ({ companyId, roleId }) => ({
        url:
          roleId !== undefined
            ? `Admintech/Admin_Messages_DML/1/${companyId}/${roleId}`
            : `Admintech/Admin_Messages_DML/1/${companyId}`,
        method: "POST",
        body: {},
      }),
    }),
    addOrEditAdminMessage: build.mutation<
      IAdminMessage[],
      {
        companyId: number;
        roleId: number;
        message: Partial<IAdminMessage>;
      }
    >({
      query: ({ companyId, roleId, message }) => ({
        url: `Admintech/Admin_Messages_DML/2/${companyId}/${roleId}`,
        method: "POST",
        body: message,
      }),
    }),
    deleteAdminMessage: build.mutation<
      IAdminMessage[],
      {
        companyId: number;
        roleId: number;
        message: IAdminMessage;
      }
    >({
      query: ({ companyId, roleId, message }) => ({
        url: `Admintech/Admin_Messages_DML/3/${companyId}/${roleId}`,
        method: "POST",
        body: message,
      }),
    }),
    bulkCreateAdminMessages: build.mutation<
      IAdminMessage[],
      {
        companyId: number;
        roleId: number;
        title: string;
        desc: string;
        recipientUserIds: number[];
        sendSms: boolean;
        sendEmail: boolean;
      }
    >({
      query: ({ companyId, roleId, ...body }) => ({
        url: `Admintech/Admin_Messages_Bulk/${companyId}/${roleId}`,
        method: "POST",
        body,
      }),
    }),
    getExercises: build.mutation<TExercise[], void>({
      query: () => ({
        url: `Admintech/Admin_Exercises_DML/1`,
        method: "POST",
        body: {},
      }),
    }),
    addOrEditExercises: build.mutation<
      TExercise[],
      {
        exercise: TExercise;
      }
    >({
      query: ({ exercise }) => ({
        url: `Admintech/Admin_Exercises_DML/2`,
        method: "POST",
        body: exercise,
      }),
    }),
    deleteExercises: build.mutation<
      TExercise[],
      {
        exercise: TExercise;
      }
    >({
      query: ({ exercise }) => ({
        url: `Admintech/Admin_Exercises_DML/3`,
        method: "POST",
        body: exercise,
      }),
    }),
    getExercisesCategories: build.mutation<
      TExercisesCategory[],
      { companyId: number }
    >({
      query: ({ companyId }) => ({
        url: `Admintech/Admin_ExercisesCategories_DML/1/${companyId}`,
        method: "POST",
        body: {},
      }),
    }),
    addOrEditExercisesCategories: build.mutation<
      TExercisesCategory[],
      {
        companyId: number;
        category: TExercisesCategory;
      }
    >({
      query: ({ companyId, category }) => ({
        url: `Admintech/Admin_ExercisesCategories_DML/2/${companyId}`,
        method: "POST",
        body: category,
      }),
    }),
    deleteExercisesCategories: build.mutation<
      TExercisesCategory[],
      {
        companyId: number;
        categoryId: string | number;
      }
    >({
      query: ({ companyId, categoryId }) => ({
        url: `Admintech/Admin_ExercisesCategories_DML/3/${companyId}`,
        method: "POST",
        body: { id: categoryId },
      }),
    }),
    //
    getQuestionnaireCompaniesHeaders: build.query<
      IQuestionnaireCompaniesHeaders[],
      { companyId: number }
    >({
      query: ({ companyId }) => ({
        url: `/Admintech/Admin_QuestionnaireCompaniesHeaders_DML/1/${companyId}`,
        method: "POST",
        body: {},
      }),
    }),
    //
    addOrEditQuestionnaireCompaniesHeaders: build.mutation<
      IQuestionnaireCompaniesHeaders[],
      {
        companyId: number;
        body: IQuestionnaireCompaniesHeaders;
      }
    >({
      query: ({ companyId, body }) => ({
        url: `Admintech/Admin_QuestionnaireCompaniesHeaders_DML/2/${companyId}`,
        method: "POST",
        body,
      }),
    }),
    deleteQuestionnaireCompaniesHeaders: build.mutation<
      IQuestionnaireCompaniesHeaders[],
      {
        companyId: number;
        body: IQuestionnaireCompaniesHeaders;
      }
    >({
      query: ({ companyId, body }) => ({
        url: `Admintech/Admin_QuestionnaireCompaniesHeaders_DML/3/${companyId}`,
        method: "POST",
        body,
      }),
    }),
    getQuestionnaireCompanies: build.mutation<
      //protocol components
      IQuestionnaireCompanies[],
      { headerId: number }
    >({
      query: ({ headerId }) => ({
        url: `Admintech/Admin_QuestionnaireCompanies_DML/1/${headerId}`,
        method: "POST",
        body: { id: headerId },
      }),
    }),
    addOrEditQuestionnaireCompanies: build.mutation<
      IQuestionnaireCompanies[],
      {
        headerId: number;
        body: IQuestionnaireCompanies;
      }
    >({
      query: ({ headerId, body }) => ({
        url: `Admintech/Admin_QuestionnaireCompanies_DML/2/${headerId}`,
        method: "POST",
        body,
      }),
    }),
    deleteQuestionnaireCompanies: build.mutation<
      IQuestionnaireCompanies[],
      {
        headerId: number;
        questionnaireCompanies: IQuestionnaireCompanies;
      }
    >({
      query: ({ headerId, questionnaireCompanies }) => ({
        url: `Admintech/Admin_QuestionnaireCompanies_DML/3/${headerId}`,
        method: "POST",
        body: questionnaireCompanies,
      }),
    }),
    getAllMetaTables: build.query<IAppointmentsProgramsMetaData, number>({
      query: (companyId) => ({
        url: `Appointments/AppointmentsPrograms_GetAllMetaTables/${companyId}`,
        method: "GET",
      }),
    }),
    getAppointmentsProgramsByCompanyId: build.query<
      TGetPendingAppointmentsProgramsResponse,
      number
    >({
      query: (companyId) => ({
        url: `Appointments/GetPendingAppointmentsProgramsByCompanyId/${companyId}`,
        method: "GET",
      }),
      providesTags: ["PendingAppointmentsPrograms"],
    }),
    getAppointmentsByCompanyAndMonth: build.query<
      any[],
      { companyId: number; month: number; year: number; fakeCompanyId?: number }
    >({
      query: ({ companyId, month, year, fakeCompanyId }) => ({
        url: `Appointments/GetAppointmentsByCompanyAndMonth/${companyId}/${month}/${year}${
          fakeCompanyId ? `/${fakeCompanyId}` : ""
        }`,
        method: "GET",
      }),
      keepUnusedDataFor: 300, // 5 minutes cache
    }),

    getAppointmentsProgramsByUserId: build.mutation<
      TAppointmentsProgramsData[],
      { companyId: number; usersGuid: string }
    >({
      query: (body) => ({
        url: `Appointments/AppointmentsPrograms_PostAppointments/1`, //1 - Type here
        method: "POST",
        body: { ...body, name: "" },
      }),
    }),
    getAppointmentsProgramsByTherapistsId: build.mutation<
      TAppointmentsProgramsData[],
      { companyId: number; therapistsGuid: string }
    >({
      query: (body) => ({
        url: `Appointments/AppointmentsPrograms_PostAppointments/1`, //1 - Type here
        method: "POST",
        body: { ...body, name: "" },
      }),
    }),
    getCompanyAppointmentsPrograms: build.mutation<
      TAppointmentsProgramsData[],
      { companyId: number }
    >({
      query: (body) => ({
        url: `Appointments/AppointmentsPrograms_PostAppointments/1`,
        method: "POST",
        body: { ...body, name: "" },
      }),
    }),
    postAppointmentsPrograms: build.mutation<
      TAppointmentsProgramsData[],
      Partial<IAppointmentsProgram>
    >({
      query: (body) => ({
        url: `Appointments/AppointmentsPrograms_PostAppointments/2`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["PendingAppointmentsPrograms"],
    }),
    deleteAppointmentsPrograms: build.mutation<
      TAppointmentsProgramsData[],
      IAppointmentsProgram
    >({
      query: (body) => ({
        url: `Appointments/AppointmentsPrograms_PostAppointments/3`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["PendingAppointmentsPrograms"],
    }),
    getAppointmentsProgramsTherapists: build.mutation<
      TAppointmentsProgramsTherapist[],
      Partial<IAppointmentsProgram>
    >({
      query: (body) => ({
        url: `Appointments/AppointmentsPrograms_PostAppointments/4`,
        method: "POST",
        body,
      }),
    }),
    getAppointmentsProgramsUnavailableTherapists: build.mutation<
      TAppointmentsProgramsTherapist[],
      Partial<IAppointmentsProgram>
    >({
      query: (body) => ({
        url: `Appointments/AppointmentsPrograms_PostAppointments/14`,
        method: "POST",
        body,
      }),
    }),
    updateTherapistTakingTreatmentPlan: build.mutation<
      TAppointmentsProgramsScheduleResponse[],
      Partial<IAppointmentsProgram>
    >({
      query: (treatmentPlan) => ({
        url: `/Appointments/AppointmentsPrograms_PostAppointments/6`,
        method: "POST",
        body: treatmentPlan,
      }),
      invalidatesTags: ["PendingAppointmentsPrograms"],
    }),
    postTreatmentPlanAutoAssign: build.mutation<
      TAppointmentsProgramsScheduleResponse[],
      Partial<IAppointmentsProgram>
    >({
      query: (treatmentPlan) => ({
        url: `/Appointments/AppointmentsPrograms_PostAppointments/66`,
        method: "POST",
        body: treatmentPlan,
      }),
      invalidatesTags: ["PendingAppointmentsPrograms"],
    }),
    updateUnavailableTherapistTakingTreatmentPlan: build.mutation<
      TAppointmentsProgramsScheduleResponse[],
      Partial<IAppointmentsProgram>
    >({
      query: (treatmentPlan) => ({
        url: `/Appointments/AppointmentsPrograms_PostAppointments/15`,
        method: "POST",
        body: treatmentPlan,
      }),
      invalidatesTags: ["PendingAppointmentsPrograms"],
    }),
    getAppointmentsProgramsSchedule: build.mutation<
      TAppointmentsProgramsScheduleResponse[],
      { id: number }
    >({
      query: (body) => ({
        url: `Appointments/AppointmentsPrograms_PostAppointments/10`,
        method: "POST",
        body,
      }),
    }),
    deleteAppointment: build.mutation<
      IAppointment[],
      Partial<IAppointmentsProgram>
    >({
      query: (body) => ({
        url: `Appointments/AppointmentsPrograms_PostAppointments/9`,
        method: "POST",
        body,
      }),
    }),
    deleteAppointmentsProgramWithAllAppointments: build.mutation<
      IAppointment[],
      { id: number; updateUsersId: number | string }
    >({
      query: (body) => ({
        url: `Appointments/AppointmentsPrograms_PostAppointments/11`,
        method: "POST",
        body,
      }),
    }),
    validateAppointmentChange: build.mutation<
      {
        success: boolean;
        conflicts: Array<{
          type: string;
          message: string;
          severity: 'warning' | 'info';
        }>;
        hasConflicts: boolean;
        canProceed: boolean;
        therapistAvailability: Array<{
          dayId: number;
          startTime: string;
          endTime: string;
        }>;
      },
      {
        appointmentId: number;
        therapistsGuid?: string;
        usersGuid?: string;
        date?: string;
        startTime?: string;
        endTime?: string;
      }
    >({
      query: (body) => ({
        url: `Appointments/ValidateChange`,
        method: "POST",
        body,
      }),
    }),
    postAppointment: build.mutation<
      any,
      {
        appointmentDate: string;
        startTime: string;
        endTime: string;
        therapistsId: number;
        therapistsGuid?: string | null;
        usersId: number | null;
        usersGuid?: string | null;
        mediaId: number | null;
        comments?: string;
        companyId: number;
      }
    >({
      query: (body) => ({
        url: `Appointments/Appointments_PostAppointments/2`,
        method: "POST",
        body: { ...body, id: 0, appointmentsTypeId: 1, statusId: 1 },
      }),
      invalidatesTags: ["PendingAppointmentsPrograms"],
    }),
    createAppointmentFast: build.mutation<any, ICreateAppointmentFastRequest>({
      query: ({ countOfAppointments, repeatCount, ...body }) => {
        const sessionCount = countOfAppointments ?? repeatCount ?? 1;
        return {
          url: `Appointments/CreateAppointmentFast`,
          method: "POST",
          body: {
            ...body,
            countOfAppointments: sessionCount,
            id: 0,
            appointmentsTypeId: 1,
            statusId: 1,
          },
          params: { repeatCount: sessionCount },
        };
      },
      invalidatesTags: ["PendingAppointmentsPrograms"],
    }),
    patchAppointment: build.mutation<
      any,
      {
        id: number;
        appointmentDate?: string;
        startTime?: string;
        endTime?: string;
        therapistsId?: number;
        therapistsGuid?: string;
        mediaId?: number;
        treatmentRoomId?: number;
        comments?: string;
        statusId?: number;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `Appointments/${id}`,
        method: "PATCH",
        body: [
          ...(body.appointmentDate ? [{ op: "replace", path: "/appointmentDate", value: body.appointmentDate }] : []),
          ...(body.startTime ? [{ op: "replace", path: "/startTime", value: body.startTime }] : []),
          ...(body.endTime ? [{ op: "replace", path: "/endTime", value: body.endTime }] : []),
          ...(body.therapistsId ? [{ op: "replace", path: "/therapistsId", value: body.therapistsId }] : []),
          ...(body.therapistsGuid ? [{ op: "replace", path: "/therapistsGuid", value: body.therapistsGuid }] : []),
          ...(body.mediaId ? [{ op: "replace", path: "/mediaId", value: body.mediaId }] : []),
          ...(body.treatmentRoomId ? [{ op: "replace", path: "/treatmentRoomId", value: body.treatmentRoomId }] : []),
          ...(body.comments ? [{ op: "replace", path: "/comments", value: body.comments }] : []),
          ...(body.statusId !== undefined ? [{ op: "replace", path: "/statusId", value: body.statusId }] : []),
        ],
      }),
      invalidatesTags: ["PendingAppointmentsPrograms"],
    }),
    getSeriesAppointments: build.query<
      Array<{
        id: number;
        appointmentDate: string | null;
        startTime: string | null;
        endTime: string | null;
        statusId: number | null;
        seriesId: string;
      }>,
      string
    >({
      query: (seriesId) => `Appointments/Series/${seriesId}`,
      providesTags: (_result, _err, seriesId) => [{ type: "AppointmentSeries" as const, id: seriesId }],
    }),
    extendSeries: build.mutation<{ success: boolean; added: number }, { seriesId: string; count: number }>({
      query: ({ seriesId, count }) => ({
        url: `Appointments/Series/${seriesId}/Extend?count=${count}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _err, { seriesId }) => [{ type: "AppointmentSeries" as const, id: seriesId }],
    }),
    getProgramSessions: build.query<IProgramSessionsResponse, number>({
      query: (programId) => `Appointments/Programs/${programId}/Sessions`,
      providesTags: (_result, _err, programId) => [
        { type: "AppointmentProgramSessions" as const, id: programId },
      ],
    }),
    addProgramSessions: build.mutation<
      IProgramSessionsResponse,
      { programId: number; count: number }
    >({
      query: ({ programId, count }) => ({
        url: `Appointments/Programs/${programId}/Sessions?count=${count}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _err, { programId }) => [
        { type: "AppointmentProgramSessions" as const, id: programId },
      ],
    }),
    deleteProgramSession: build.mutation<
      IProgramSessionsResponse,
      { programId: number; appointmentId: number }
    >({
      query: ({ programId, appointmentId }) => ({
        url: `Appointments/Programs/${programId}/Sessions/${appointmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _err, { programId }) => [
        { type: "AppointmentProgramSessions" as const, id: programId },
      ],
    }),
    getProgramConfig: build.query<IProgramConfig, number>({
      query: (programId) => `Appointments/Programs/${programId}/Config`,
    }),
    convertToSeries: build.mutation<
      IProgramSessionsResponse,
      { appointmentId: number; body: IConvertToSeriesRequest }
    >({
      query: ({ appointmentId, body }) => ({
        url: `Appointments/${appointmentId}/ConvertToSeries`,
        method: "POST",
        body,
      }),
    }),
    getAllCombosForSearchUsers: build.query<IAppointment, number | string>({
      query: (companyId) => ({
        url: `user/GetAllCombosForSearchUsers/${companyId}`,
        method: "GET",
      }),
    }),
    confirmAccessToUsers: build.query<
      IAppointment,
      {
        // usersId: number | string;
        clientUsersGuid: number | string;
      }
    >({
      query: ({ clientUsersGuid }) => ({
        url: `General/General_ConfirmRead/2/1/${clientUsersGuid}`,
        method: "GET",
      }),
    }),
    getAllOptionsDataTypes: build.query<TDataType[], void>({
      query: () => ({
        url: `General/Gen_GetTableByTableName/DataTypes`,
        method: "GET",
      }),
    }),
    getAllCompanies: build.query<TCompany[], void>({
      query: () => ({
        url: `General/Gen_GetTableByTableName/Companies`,
        method: "GET",
      }),
    }),
    getAllQuestionsSpecificTypes: build.query<TQuestionsSpecificTypes[], void>({
      query: () => ({
        url: `General/Gen_GetTableByTableName/QuestionsSpecificTypes`,
        method: "GET",
      }),
    }),
    getAllQuestionsUsersFields: build.query<TQuestionsUsersFields[], void>({
      query: () => ({
        url: `General/Gen_GetTableByTableName/QuestionsUsersFields`,
        method: "GET",
      }),
    }),
    getSpecialQuestionsDisplay: build.query<{ id: number; desc: string }[], void>({
      query: () => ({
        url: `Questions/GetSpecialQuestionsDisplay`,
        method: "GET",
      }),
    }),
    getAllCities: build.query<TCity[], void>({
      query: () => ({
        url: `General/Gen_GetTableByTableName/Cities`,
        method: "GET",
      }),
    }),
    getQuestionnaireCompaniesTypesByCompanyId: build.mutation<
      IQuestionnaireCompaniesTypes[],
      number | string
    >({
      query: (companyId) => ({
        url: `Admintech/Admin_QuestionnaireCompaniesTypes_DML/1/${companyId}`,
        method: "POST",
        body: {},
      }),
    }),
    postQuestionnaireCompaniesTypes: build.mutation<
      IQuestionnaireCompaniesTypes[],
      { companyId: string | number; body: IQuestionnaireCompaniesTypes }
    >({
      query: ({ companyId, body }) => ({
        url: `Admintech/Admin_QuestionnaireCompaniesTypes_DML/2/${companyId}`,
        method: "POST",
        body,
      }),
    }),
    deleteQuestionnaireCompaniesTypes: build.mutation<
      IQuestionnaireCompaniesTypes[],
      { companyId: string | number; body: TSmsMessage }
    >({
      query: ({ companyId, body }) => ({
        url: `Admintech/Admin_QuestionnaireCompaniesTypes_DML/3/${companyId}`,
        method: "POST",
        body,
      }),
    }),
    getAllSmsMessages: build.mutation<TSmsMessage[], number | string>({
      query: (companyId) => ({
        url: `Admintech/Admin_SMSMessagesList_DML/1/${companyId}`,
        method: "POST",
        body: { title: "", body: "" },
      }),
    }),

    postSmsMessage: build.mutation<
      TSmsMessage[],
      { companyId: string | number; body: TSmsMessage }
    >({
      query: ({ companyId, body }) => ({
        url: `Admintech/Admin_SMSMessagesList_DML/2/${companyId}`,
        method: "POST",
        body,
      }),
    }),
    getDiagramsTypes: build.query<TNamedItem[], void>({
      query: () => ({
        url: `/dashboards/getDiagramsTypes`,
        method: "GET",
      }),
    }),
    deleteSmsMessage: build.mutation<
      TSmsMessage[],
      { companyId: string | number; body: TSmsMessage }
    >({
      query: ({ companyId, body }) => ({
        url: `Admintech/Admin_SMSMessagesList_DML/3/${companyId}`,
        method: "POST",
        body,
      }),
    }),

    getAllPlans: build.query<TPlan[], { lang?: string } | void>({
      query: (params) => {
        const url =
          params && params.lang
            ? `Admintech/Admin_Plans_DML/1/`
            : `Admintech/Admin_Plans_DML/1/`;
        return {
          url,
          method: "POST",
          body: { Id: 0, Name: "" },
        };
      },
    }),
    postPlan: build.mutation<TPlan[], { body: TPlan }>({
      query: ({ body }) => ({
        url: `Admintech/Admin_Plans_DML/2`,
        method: "POST",
        body,
      }),
    }),

    deletePlan: build.mutation<TPlan[], { body: TPlan }>({
      query: ({ body }) => ({
        url: `Admintech/Admin_Plans_DML/3`,
        method: "POST",
        body,
      }),
    }),
    getPlanPrices: build.query<TPlanPrice[], number>({
      query: (planId) => ({
        url: `Admintech/Admin_PlansPrices_DML/1/${planId}`,
        method: "POST",
        body: [],
      }),
    }),
    postPlanPrices: build.mutation<
      TPlanPrice[],
      { planId: number; prices: TPlanPrice[] }
    >({
      query: ({ planId, prices }) => ({
        url: `Admintech/Admin_PlansPrices_DML/2/${planId}`,
        method: "POST",
        body: prices,
      }),
    }),
    deletePlanPrice: build.mutation<
      TPlanPrice[],
      { planId: number; price: TPlanPrice }
    >({
      query: ({ planId, price }) => ({
        url: `Admintech/Admin_PlansPrices_DML/3/${planId}`,
        method: "POST",
        body: price,
      }),
    }),
    getLinkForQuestionnaire: build.query<{ link: string }, number>({
      query: (id) => ({
        url: `Admintech/Admin_GetLinkForQuestionnaire/${id}`,
        method: "GET",
      }),
    }),
    getClinicalUserSummary: build.query<TClinicSummaryResponce, string>({
      query: (usersGuid) => ({
        url: `user/ClinicalSummary_userSummary/${usersGuid}`,
        method: "GET",
      }),
    }),
    uploadCompanyLogo: build.mutation<
      { urls: string[]; fileIds?: string[] },
      { file: File; companyId: number }
    >({
      query: ({ file, companyId }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: `Files/UploadFiles/6/${companyId}`,
          method: "POST",
          body: formData,
          formData: true,
        };
      },
    }),
    getAllFeatures: build.query<TFeature[], void>({
      query: () => ({
        url: `Admintech/Admin_Features_DML/1`,
        method: "POST",
        body: {},
      }),
    }),
    postFeature: build.mutation<TFeature[], { body: TFeature }>({
      query: ({ body }) => ({
        url: `Admintech/Admin_Features_DML/2`,
        method: "POST",
        body,
      }),
    }),

    deleteFeature: build.mutation<TFeature[], { body: TFeature }>({
      query: ({ body }) => ({
        url: `Admintech/Admin_Features_DML/3`,
        method: "POST",
        body,
      }),
    }),
    getAllFeatures2Plan: build.query<TFeatures2Plans[], number>({
      query: (planId) => ({
        url: `Admintech/Admin_Features2Plans_DML/1/${planId}`,
        method: "POST",
        body: [],
      }),
    }),
    postFeatures2Plan: build.mutation<
      TFeatures2Plans[],
      {
        list: TFeatures2Plans[];
        planId: number;
      }
    >({
      query: ({ list, planId }) => ({
        url: `Admintech/Admin_Features2Plans_DML/2/${planId}`,
        method: "POST",
        body: list,
      }),
    }),

    deleteFeatures2Plan: build.mutation<
      TFeatures2Plans[],
      {
        list: TFeatures2Plans[];
        planId: number;
      }
    >({
      query: ({ list, planId }) => ({
        url: `Admintech/Admin_Features_DML/3/${planId}`,
        method: "POST",
        body: list,
      }),
    }),
    getAllCurrencies: build.query<TCurrency[], void>({
      query: () => ({
        url: "Admintech/Admin_Currency_DML/1",
        method: "POST",
        body: { CurrencyCode: "", CurrencyName: "" },
      }),
    }),
    postCurrency: build.mutation<TCurrency[], TCurrency>({
      query: (currency) => ({
        url: `Admintech/Admin_Currency_DML/2`,
        method: "POST",
        body: currency,
      }),
    }),
    deleteCurrency: build.mutation<TCurrency[], number>({
      query: (id) => ({
        url: `Admintech/Admin_Currency_DML/3`,
        method: "POST",
        body: { id },
      }),
    }),
    // Company Services endpoints (CRUD via AdminTech)
    // Type: 1=Select, 2=Insert/Update, 3=Delete
    getCompanyServices: build.query<
      {
        CompanyServicesListResults: Array<{
          id: number;
          companyId: number;
          code: string;
          name: string;
          statusId: number;
        }>;
        TherapistsList: Array<{
          id: number;
          firstName: string;
          lastName: string;
          email: string;
          companyId: number;
        }>;
        CurrencyList: Array<{
          id: number;
          currencyCode: string;
          currencyName: string;
        }>;
      },
      { companyId: number }
    >({
      query: ({ companyId }) => ({
        url: `Admintech/Admin_CompanyServices_DML/1`,
        method: "POST",
        body: { companyId },
      }),
      transformResponse: (response: {
        CompanyServicesListResults?: Array<{
          Id?: number;
          CompanyId?: number;
          Code?: string;
          Name?: string;
          StatusId?: number;
          id?: number;
          companyId?: number;
          code?: string;
          name?: string;
          statusId?: number;
        }>;
        TherapistsList?: Array<{
          Id?: number;
          FirstName?: string;
          LastName?: string;
          Email?: string;
          CompanyId?: number;
          id?: number;
          firstName?: string;
          lastName?: string;
          email?: string;
          companyId?: number;
        }>;
        CurrencyList?: Array<{
          Id?: number;
          CurrencyCode?: string;
          CurrencyName?: string;
          id?: number;
          currencyCode?: string;
          currencyName?: string;
        }>;
      }) => ({
        CompanyServicesListResults: (
          response.CompanyServicesListResults || []
        ).map((s) => ({
          id: s.id ?? s.Id ?? 0,
          companyId: s.companyId ?? s.CompanyId ?? 0,
          code: s.code ?? s.Code ?? "",
          name: s.name ?? s.Name ?? "",
          statusId: s.statusId ?? s.StatusId ?? 0,
        })),
        TherapistsList: (response.TherapistsList || []).map((t) => ({
          id: t.id ?? t.Id ?? 0,
          firstName: t.firstName ?? t.FirstName ?? "",
          lastName: t.lastName ?? t.LastName ?? "",
          email: t.email ?? t.Email ?? "",
          companyId: t.companyId ?? t.CompanyId ?? 0,
        })),
        CurrencyList: (response.CurrencyList || []).map((c) => ({
          id: c.id ?? c.Id ?? 0,
          currencyCode: c.currencyCode ?? c.CurrencyCode ?? "",
          currencyName: c.currencyName ?? c.CurrencyName ?? "",
        })),
      }),
      providesTags: (result) =>
        result?.CompanyServicesListResults
          ? [
              ...result.CompanyServicesListResults.map((s) => ({
                type: "CompanyServices" as const,
                id: s.id,
              })),
              { type: "CompanyServices" as const, id: "LIST" },
            ]
          : [{ type: "CompanyServices" as const, id: "LIST" }],
    }),
    createOrUpdateCompanyService: build.mutation<
      {
        CompanyServicesListResults: Array<{
          id: number;
          companyId: number;
          name: string;
          statusId: number;
        }>;
        TherapistsList: Array<{
          id: number;
          firstName: string;
          lastName: string;
          email: string;
          companyId: number;
        }>;
        CurrencyList: Array<{
          id: number;
          currencyCode: string;
          currencyName: string;
        }>;
      },
      {
        id?: number;
        companyId: number;
        name: string;
        statusId?: number;
      }
    >({
      query: (body) => ({
        url: `Admintech/Admin_CompanyServices_DML/2`,
        method: "POST",
        body: { ...body, statusId: body.statusId ?? 1 },
      }),
      invalidatesTags: [{ type: "CompanyServices" as const, id: "LIST" }],
    }),
    deleteCompanyService: build.mutation<
      {
        CompanyServicesListResults: Array<{
          id: number;
          companyId: number;
          name: string;
          statusId: number;
        }>;
        TherapistsList: Array<{
          id: number;
          firstName: string;
          lastName: string;
          email: string;
          companyId: number;
        }>;
        CurrencyList: Array<{
          id: number;
          currencyCode: string;
          currencyName: string;
        }>;
      },
      { id: number; companyId: number }
    >({
      query: (body) => ({
        url: `Admintech/Admin_CompanyServices_DML/3`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "CompanyServices" as const, id: "LIST" }],
    }),
    // Company Services Prices endpoints (CRUD via AdminTech)
    // Type: 1=Select, 2=Insert/Update, 3=Delete
    getCompanyServicesPrices: build.query<
      Array<{
        csp: {
          id: number;
          companyId: number;
          companyServicesId: number;
          therapistId: number | null;
          priceBeforeTax: number;
          priceAfterTax: number;
          currencyId: number;
          statusId: number;
        };
        cs: {
          id: number;
          companyId: number;
          code: string;
          name: string;
          statusId: number;
        } | null;
        t: {
          id: number;
          firstName: string;
          lastName: string;
          email: string;
          companyId: number;
        } | null;
      }>,
      { companyId: number }
    >({
      query: ({ companyId }) => ({
        url: `Admintech/Admin_CompanyServicesPrices_DML/1`,
        method: "POST",
        body: { companyId },
      }),
      transformResponse: (
        response: Array<{
          // Handle both PascalCase (C# default) and camelCase (already transformed)
          Csp?: {
            Id: number;
            CompanyId: number;
            CompanyServicesId: number;
            TherapistId: number | null;
            PriceBeforeTax: number;
            PriceAfterTax: number;
            CurrencyId: number;
            StatusId: number;
          };
          csp?: {
            id: number;
            companyId: number;
            companyServicesId: number;
            therapistId: number | null;
            priceBeforeTax: number;
            priceAfterTax: number;
            currencyId: number;
            statusId: number;
          };
          Cs?: {
            Id: number;
            CompanyId: number;
            Code: string;
            Name: string;
            StatusId: number;
          } | null;
          cs?: {
            id: number;
            companyId: number;
            code: string;
            name: string;
            statusId: number;
          } | null;
          T?: {
            Id: number;
            FirstName: string;
            LastName: string;
            Email: string;
            CompanyId: number;
          } | null;
          t?: {
            id: number;
            firstName: string;
            lastName: string;
            email: string;
            companyId: number;
          } | null;
        }>
      ) =>
        (response || []).map((item) => {
          // Check if data is already camelCase (from backend) or PascalCase
          const cspData = item.csp || item.Csp;
          const csData = item.cs || item.Cs;
          const tData = item.t || item.T;

          return {
            csp: cspData
              ? {
                  id: "id" in cspData ? cspData.id : cspData.Id,
                  companyId:
                    "companyId" in cspData
                      ? cspData.companyId
                      : cspData.CompanyId,
                  companyServicesId:
                    "companyServicesId" in cspData
                      ? cspData.companyServicesId
                      : cspData.CompanyServicesId,
                  therapistId:
                    "therapistId" in cspData
                      ? cspData.therapistId
                      : cspData.TherapistId,
                  priceBeforeTax:
                    "priceBeforeTax" in cspData
                      ? cspData.priceBeforeTax
                      : cspData.PriceBeforeTax,
                  priceAfterTax:
                    "priceAfterTax" in cspData
                      ? cspData.priceAfterTax
                      : cspData.PriceAfterTax,
                  currencyId:
                    "currencyId" in cspData
                      ? cspData.currencyId
                      : cspData.CurrencyId,
                  statusId:
                    "statusId" in cspData ? cspData.statusId : cspData.StatusId,
                }
              : {
                  id: 0,
                  companyId: 0,
                  companyServicesId: 0,
                  therapistId: null,
                  priceBeforeTax: 0,
                  priceAfterTax: 0,
                  currencyId: 0,
                  statusId: 0,
                },
            cs: csData
              ? {
                  id: "id" in csData ? csData.id : csData.Id,
                  companyId:
                    "companyId" in csData ? csData.companyId : csData.CompanyId,
                  code: "code" in csData ? csData.code : csData.Code,
                  name: "name" in csData ? csData.name : csData.Name,
                  statusId:
                    "statusId" in csData ? csData.statusId : csData.StatusId,
                }
              : null,
            t: tData
              ? {
                  id: "id" in tData ? tData.id : tData.Id,
                  firstName:
                    "firstName" in tData ? tData.firstName : tData.FirstName,
                  lastName:
                    "lastName" in tData ? tData.lastName : tData.LastName,
                  email: "email" in tData ? tData.email : tData.Email,
                  companyId:
                    "companyId" in tData ? tData.companyId : tData.CompanyId,
                }
              : null,
          };
        }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((p) => ({
                type: "CompanyServicesPrices" as const,
                id: p.csp.id,
              })),
              { type: "CompanyServicesPrices" as const, id: "LIST" },
            ]
          : [{ type: "CompanyServicesPrices" as const, id: "LIST" }],
    }),
    createOrUpdateCompanyServicePrice: build.mutation<
      Array<{
        csp: {
          id: number;
          companyId: number;
          companyServicesId: number;
          therapistId: number | null;
          priceBeforeTax: number;
          priceAfterTax: number;
          currencyId: number;
          statusId: number;
        };
        cs: {
          id: number;
          companyId: number;
          name: string;
          statusId: number;
        } | null;
        t: {
          id: number;
          firstName: string;
          lastName: string;
          email: string;
          companyId: number;
        } | null;
      }>,
      {
        id?: number;
        companyId: number;
        companyServicesId: number;
        therapistId: number | null;
        priceBeforeTax: number;
        priceAfterTax: number;
        currencyId: number;
        statusId?: number;
      }
    >({
      query: (body) => ({
        url: `Admintech/Admin_CompanyServicesPrices_DML/2`,
        method: "POST",
        body: { ...body, statusId: body.statusId ?? 1 },
      }),
      invalidatesTags: [
        { type: "CompanyServicesPrices" as const, id: "LIST" },
        { type: "CompanyServices" as const, id: "LIST" },
      ],
    }),
    deleteCompanyServicePrice: build.mutation<
      Array<{
        csp: {
          id: number;
          companyId: number;
          companyServicesId: number;
          therapistId: number | null;
          priceBeforeTax: number;
          priceAfterTax: number;
          currencyId: number;
          statusId: number;
        };
        cs: {
          id: number;
          companyId: number;
          name: string;
          statusId: number;
        } | null;
        t: {
          id: number;
          firstName: string;
          lastName: string;
          email: string;
          companyId: number;
        } | null;
      }>,
      { id: number; companyId: number }
    >({
      query: (body) => ({
        url: `Admintech/Admin_CompanyServicesPrices_DML/3`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "CompanyServicesPrices" as const, id: "LIST" },
        { type: "CompanyServices" as const, id: "LIST" },
      ],
    }),
    // PatientDefaultServicePrice endpoints
    getPatientDefaultServicePrices: build.query<
      Array<{
        id: number;
        userGuid: string;
        companyServicesPricesId: number;
        companyServicesId: number;
      }>,
      void
    >({
      query: () => ({
        url: `Admintech/Admin_PatientDefaultServicePrice_DML/1`,
        method: "POST",
        body: {},
      }),
      transformResponse: (
        response: Array<{
          Id?: number; id?: number;
          UserGuid?: string; userGuid?: string;
          CompanyServicesPricesId?: number; companyServicesPricesId?: number;
          CompanyServicesId?: number; companyServicesId?: number;
        }>
      ) => {
        if (!response) return [];
        return response.map((item) => ({
          id: item.Id ?? item.id ?? 0,
          userGuid: item.UserGuid ?? item.userGuid ?? "",
          companyServicesPricesId: item.CompanyServicesPricesId ?? item.companyServicesPricesId ?? 0,
          companyServicesId: item.CompanyServicesId ?? item.companyServicesId ?? 0,
        }));
      },
      providesTags: [{ type: "PatientDefaultServicePrice" as const, id: "LIST" }],
    }),
    createPatientDefaultServicePrices: build.mutation<
      void,
      { companyServicesPricesId: number; userGuids: string[] }
    >({
      query: (body) => ({
        url: `Admintech/Admin_PatientDefaultServicePrice_DML/2`,
        method: "POST",
        body: {
          CompanyServicesPricesId: body.companyServicesPricesId,
          UserGuids: body.userGuids,
        },
      }),
      invalidatesTags: [{ type: "PatientDefaultServicePrice" as const, id: "LIST" }],
    }),
    deletePatientDefaultServicePrice: build.mutation<
      void,
      { id: number }
    >({
      query: (body) => ({
        url: `Admintech/Admin_PatientDefaultServicePrice_DML/3`,
        method: "POST",
        body: { Id: body.id },
      }),
      invalidatesTags: [{ type: "PatientDefaultServicePrice" as const, id: "LIST" }],
    }),
    getProtocolsByCompany: build.query<
      IQuestionnaireCompaniesHeaders[],
      { companyId: number }
    >({
      query: ({ companyId }) => ({
        url: `/Admintech/Admin_QuestionnaireCompaniesHeaders_DML/1/${companyId}`,
        method: "POST",
        body: {},
      }),
    }),
    getProtocolComponents: build.query<
      IQuestionnaireCompanies[],
      { protocolId: number }
    >({
      query: ({ protocolId }) => ({
        url: `/Admintech/Admin_QuestionnaireCompanies_DML/1/${protocolId}`,
        method: "POST",
        body: { id: protocolId },
      }),
    }),
    getPaymentsByCompanyId: build.query<
      IPaymentResponse,
      { type: number; companyId: number }
    >({
      query: ({ type = 1, companyId }) => ({
        url: `Payments/Admin_Payments_DML/${type}/${companyId}`,
        method: "GET",
      }),
      transformResponse: normalizePaymentsApiResponse,
      providesTags: (_result, _error, { companyId }) => [
        { type: "CompanySubscription" as const, id: companyId },
      ],
    }),
    getCompanyPaymentsSummary: build.query<
      IFinancialDashboardResponse,
      { companyId: number; startDate?: string; endDate?: string; includeSubCompanies?: boolean }
    >({
      query: ({ companyId, startDate, endDate, includeSubCompanies = true }) => ({
        url: `Dashboards/GetCompanyPaymentsSummary`,
        method: "GET",
        params: {
          companyId,
          startDate,
          endDate,
          includeSubCompanies,
        },
      }),
      keepUnusedDataFor: 300,
    }),
    // User payments endpoint - uses the same endpoint as Admin_Payments_DML with optional userId parameter
    getPaymentsByUserId: build.query<
      IPaymentResponse,
      { type: number; companyId: number; userId: number }
    >({
      query: ({ type = 1, companyId, userId }) => ({
        url: `Payments/Admin_Payments_DML/${type}/${companyId}/${userId}`,
        method: "GET",
      }),
      transformResponse: normalizePaymentsApiResponse,
      providesTags: (_result, _error, { userId }) => [
        { type: "UserPayments" as const, id: userId },
      ],
    }),
    getUserBalanceHistory: build.query<
      IBalanceHistoryItem[],
      { userId: number }
    >({
      query: ({ userId }) => ({
        url: `Payments/GetUserBalanceHistory/${userId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { userId }) => [
        { type: "UserPayments" as const, id: userId },
      ],
    }),
    getCardLast4Digits: build.query<ICardLast4DigitsResponse, number>({
      query: (companyId) => ({
        url: `Payments/GetCardLast4Digits/${companyId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, companyId) => [
        { type: "CompanySubscription" as const, id: companyId },
      ],
    }),
    togglePaymentStatus: build.mutation<
      ITogglePaymentStatusResponse,
      { paymentId: number }
    >({
      query: ({ paymentId }) => ({
        url: `Payments/Admin_TogglePaymentStatus/${paymentId}`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, { paymentId }) => {
        // Invalidate the payment data to refresh the table
        return [{ type: "admin" as const, id: "payments" }];
      },
    }),
    transactionRefund: build.mutation<
      ITransactionRefundResponse,
      ITransactionRefundRequest
    >({
      query: (body) => ({
        url: `Payments/RefundByTransactionId`,
        method: "POST",
        body,
      }),
      // Transform PascalCase response from backend to camelCase expected by frontend
      transformResponse: (response: {
        ResponseCode?: number;
        responseCode?: number;
        Description?: string;
        description?: string;
        TranzactionId?: string;
        tranzactionId?: string;
        NewTranzactionId?: string;
        newTranzactionId?: string;
        DocumentNumber?: string;
        documentNumber?: string;
        DocumentUrl?: string;
        documentUrl?: string;
        DocumentType?: string;
        documentType?: string;
        Amount?: number;
        amount?: number;
        PaymentClearingCompaniesId?: number;
        paymentClearingCompaniesId?: number;
      }): ITransactionRefundResponse => ({
        responseCode: response.ResponseCode ?? response.responseCode ?? -1,
        description: response.Description ?? response.description,
        tranzactionId: response.TranzactionId ?? response.tranzactionId,
        newTranzactionId: response.NewTranzactionId ?? response.newTranzactionId,
        documentNumber: response.DocumentNumber ?? response.documentNumber,
        documentUrl: response.DocumentUrl ?? response.documentUrl,
        documentType: response.DocumentType ?? response.documentType,
        amount: response.Amount ?? response.amount,
        paymentClearingCompaniesId: response.PaymentClearingCompaniesId ?? response.paymentClearingCompaniesId,
      }),
      invalidatesTags: () => {
        // Invalidate the payment data to refresh the table
        return [{ type: "admin" as const, id: "payments" }];
      },
    }),
    bulkTransactionsRefund: build.mutation<
      IBulkRefundResponse,
      IBulkRefundRequest
    >({
      query: (body) => ({
        url: `Payments/BulkTransactionsRefund_PayPlus`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { transactionIds }) => {
        // Invalidate the payment data to refresh the table
        return [{ type: "admin" as const, id: "payments" }];
      },
    }),
    createPaymentPage: build.mutation<
      ICreatePaymentResponse,
      ICreatePaymentPageRequest
    >({
      query: (body) => ({
        url: `Payments/GetPaymentPage`,
        method: "POST",
        body,
      }),
    }),
    createBulkPaymentJob: build.mutation<
      ICreateBulkPaymentJobResponse,
      ICreateBulkPaymentJobRequest
    >({
      query: (body) => ({
        url: `Payments/CreatePaymentPageBulkJob`,
        method: "POST",
        body,
      }),
      transformResponse: normalizeBulkPaymentJobResponse,
      invalidatesTags: [{ type: "Jobs" as const, id: "LIST" }],
    }),
    getJobs: build.query<IJobListItem[], IJobListRequest | void>({
      query: (request) => {
        const params = new URLSearchParams();
        if (request?.jobType) params.set("jobType", request.jobType);
        if (request?.status) params.set("status", request.status);
        if (request?.limit) params.set("limit", request.limit.toString());
        const queryString = params.toString();

        return {
          url: `Jobs${queryString ? `?${queryString}` : ""}`,
          method: "GET",
        };
      },
      transformResponse: (response: any[]) => (response || []).map(normalizeJob),
      providesTags: (result) => [
        { type: "Jobs" as const, id: "LIST" },
        ...(result || []).map((job) => ({ type: "Jobs" as const, id: job.id })),
      ],
    }),
    getJob: build.query<IJobDetail, number>({
      query: (jobId) => ({
        url: `Jobs/${jobId}`,
        method: "GET",
      }),
      transformResponse: normalizeJobDetail,
      providesTags: (result, error, jobId) => [{ type: "Jobs" as const, id: jobId }],
    }),
    getJobItems: build.query<IJobItem[], IJobItemsRequest>({
      query: ({ jobId, status, limit }) => {
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        if (limit) params.set("limit", limit.toString());
        const queryString = params.toString();

        return {
          url: `Jobs/${jobId}/items${queryString ? `?${queryString}` : ""}`,
          method: "GET",
        };
      },
      transformResponse: (response: any[]) => (response || []).map(normalizeJobItem),
      providesTags: (result, error, { jobId }) => [{ type: "Jobs" as const, id: jobId }],
    }),
    getJobItemAttempts: build.query<IJobItemAttempt[], number>({
      query: (itemId) => ({
        url: `Jobs/items/${itemId}/attempts`,
        method: "GET",
      }),
      transformResponse: (response: any[]) => (response || []).map(normalizeJobAttempt),
      providesTags: (result, error, itemId) => [{ type: "Jobs" as const, id: `attempts-${itemId}` }],
    }),
    retryJobItem: build.mutation<IJobRetryResponse, number>({
      query: (itemId) => ({
        url: `Jobs/items/${itemId}/retry`,
        method: "POST",
      }),
      transformResponse: normalizeJobRetryResponse,
      invalidatesTags: (result, error, itemId) => [
        { type: "Jobs" as const, id: "LIST" },
        { type: "Jobs" as const, id: `attempts-${itemId}` },
      ],
    }),
    retryJobFailedItems: build.mutation<IJobRetryResponse, number>({
      query: (jobId) => ({
        url: `Jobs/${jobId}/retry-failed`,
        method: "POST",
      }),
      transformResponse: normalizeJobRetryResponse,
      invalidatesTags: (result, error, jobId) => [
        { type: "Jobs" as const, id: "LIST" },
        { type: "Jobs" as const, id: jobId },
      ],
    }),
    cancelSubscription: build.mutation<any, { companyId: number }>({
      query: ({ companyId }) => ({
        url: `Payments/CompanyPayment/3`,
        method: "POST",
        body: {
          CompanyId: companyId,
          SlikaObject: {
            CardNumber: "",
            CardDateMMyy: "",
            CardOwnerTaz: "",
            CardOwnerName: "",
          },
        },
      }),
    }),
    changeSubscriptionPlan: build.mutation<
      IPaymentLinkResponse,
      IChangeSubscriptionPlanRequest
    >({
      query: (body) => ({
        url: `Payments/CompanyPayment/2`,
        method: "POST",
        body: {
          ...body,
          slikaObject: {
            CardNumber: "",
            CardDateMMyy: "",
            CardOwnerTaz: "",
            CardOwnerName: "",
          },
        },
      }),
      invalidatesTags: (_result, _error, { companyId }) => [
        { type: "CompanySubscription" as const, id: companyId },
      ],
    }),
    changeSubscriptionCard: build.mutation<
      IPaymentLinkResponse,
      IChangeSubscriptionCardRequest
    >({
      query: (body) => ({
        url: `Payments/CompanyPayment/22`,
        method: "POST",
        body,
      }),
    }),
    getTreatmentRooms: build.query<ITreatmentRoom[], { companyId: number }>({
      query: ({ companyId }) => ({
        url: `Admintech/Admin_TreatmentRooms_DML/1/${companyId}`,
        method: "POST",
        body: { Name: "1" },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((r) => ({
                type: "TreatmentRooms" as const,
                id: r.id,
              })),
              { type: "TreatmentRooms" as const, id: "LIST" },
            ]
          : [{ type: "TreatmentRooms" as const, id: "LIST" }],
    }),
    addOrEditTreatmentRoom: build.mutation<
      ITreatmentRoom[],
      { companyId: number; body: Partial<ITreatmentRoom> }
    >({
      query: ({ companyId, body }) => ({
        url: `Admintech/Admin_TreatmentRooms_DML/2/${companyId}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              ...result.map((r) => ({
                type: "TreatmentRooms" as const,
                id: r.id,
              })),
              { type: "TreatmentRooms" as const, id: "LIST" },
            ]
          : [{ type: "TreatmentRooms" as const, id: "LIST" }],
    }),
    deleteTreatmentRoom: build.mutation<
      ITreatmentRoom[],
      { companyId: number; body: ITreatmentRoom }
    >({
      query: ({ companyId, body }) => ({
        url: `Admintech/Admin_TreatmentRooms_DML/3/${companyId}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              ...result.map((r) => ({
                type: "TreatmentRooms" as const,
                id: r.id,
              })),
              { type: "TreatmentRooms" as const, id: "LIST" },
            ]
          : [{ type: "TreatmentRooms" as const, id: "LIST" }],
    }),
    getTreatmentGroups: build.query<ITreatmentGroup[], { companyId: number }>({
      query: ({ companyId }) => ({
        url: `TreatmentGroups/getAll`,
        method: "GET",
        params: { companyId },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((g) => ({
                type: "TreatmentGroups" as const,
                id: g.id,
              })),
              { type: "TreatmentGroups" as const, id: "LIST" },
            ]
          : [{ type: "TreatmentGroups" as const, id: "LIST" }],
    }),
    getTreatmentGroupById: build.query<ITreatmentGroup, { id: number }>({
      query: ({ id }) => ({
        url: `TreatmentGroups/getById/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, { id }) => [
        { type: "TreatmentGroups" as const, id },
      ],
    }),
    createTreatmentGroup: build.mutation<
      ITreatmentGroup,
      {
        groupName: string;
        maxParticipants?: number;
        therapyTypeId?: number;
        therapistId?: number;
        companyId: number;
      }
    >({
      query: (body) => ({
        url: `TreatmentGroups/create`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "TreatmentGroups" as const, id: "LIST" }],
    }),
    updateTreatmentGroup: build.mutation<
      ITreatmentGroup,
      {
        id: number;
        groupName: string;
        maxParticipants?: number;
        therapyTypeId?: number;
        therapistId?: number;
      }
    >({
      query: (body) => ({
        url: `TreatmentGroups/update`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "TreatmentGroups" as const, id },
        { type: "TreatmentGroups" as const, id: "LIST" },
      ],
    }),
    deleteTreatmentGroup: build.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `TreatmentGroups/delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "TreatmentGroups" as const, id },
        { type: "TreatmentGroups" as const, id: "LIST" },
      ],
    }),
    addGroupMember: build.mutation<
      void,
      { treatmentGroupId: number; userId: number }
    >({
      query: (body) => ({
        url: `TreatmentGroups/addMember`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { treatmentGroupId }) => [
        { type: "TreatmentGroups" as const, id: treatmentGroupId },
        { type: "TreatmentGroups" as const, id: "MEMBERS" },
      ],
    }),
    removeGroupMember: build.mutation<
      void,
      { groupId: number; userId: number }
    >({
      query: ({ groupId, userId }) => ({
        url: `TreatmentGroups/removeMember/${groupId}/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: "TreatmentGroups" as const, id: groupId },
        { type: "TreatmentGroups" as const, id: "MEMBERS" },
      ],
    }),
    getGroupMembers: build.query<ITreatmentGroupMember[], { groupId: number }>({
      query: ({ groupId }) => ({
        url: `TreatmentGroups/getMembers/${groupId}`,
        method: "GET",
      }),
      providesTags: (result, error, { groupId }) => [
        { type: "TreatmentGroups" as const, id: groupId },
        { type: "TreatmentGroups" as const, id: "MEMBERS" },
      ],
    }),
    createGroupTreatmentPlan: build.mutation<
      ICreateGroupTreatmentPlanResponse,
      ICreateGroupTreatmentPlanRequest
    >({
      query: (body) => ({
        url: `TreatmentGroups/createGroupTreatmentPlan`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { treatmentGroupId }) => [
        { type: "TreatmentGroups" as const, id: treatmentGroupId },
        { type: "TreatmentGroups" as const, id: "LIST" },
        { type: "TreatmentGroups" as const, id: "TREATMENT_PLANS" },
      ],
    }),
    getGroupTreatmentPlans: build.query<
      TAppointmentsProgramsData[],
      { groupId: number }
    >({
      query: ({ groupId }) => ({
        url: `TreatmentGroups/getTreatmentPlans/${groupId}`,
        method: "GET",
      }),
      providesTags: (result, error, { groupId }) => [
        { type: "TreatmentGroups" as const, id: groupId },
        { type: "TreatmentGroups" as const, id: "TREATMENT_PLANS" },
      ],
    }),
    // Cancel Appointment - Get cancel reasons (Type=1) and cancel appointment (Type=2)
    getCancelAppReasons: build.query<ICancelAppReason[], void>(
      {
        query: () => ({
          url: `Appointments/Appointments_CancelApp/1`,
          method: "POST",
          body: {
            appointmentsId: 0,
            cancelAppReasonsId: 0,
            anotherReason: "",
            createdUsersId: 0,
          },
        }),
        transformResponse: (
          response: Array<{
            Id?: number;
            CompanyId?: number;
            Reason?: string;
            IsAnother?: boolean;
            id?: number;
            companyId?: number;
            reason?: string;
            isAnother?: boolean;
          }>
        ) =>
          (response || []).map((r) => ({
            id: r.id ?? r.Id ?? 0,
            companyId: r.companyId ?? r.CompanyId ?? 0,
            reason: r.reason ?? r.Reason ?? "",
            isAnother: r.isAnother ?? r.IsAnother ?? false,
          })),
      }
    ),
    cancelAppointment: build.mutation<ICancelAppReason[], ICancelAppRequest>({
      query: (body) => ({
        url: `Appointments/Appointments_CancelApp/2`,
        method: "POST",
        body,
      }),
    }),
    // Get all treatments with outstanding debt for a user
    getTreatmentsWithoutPayment: build.query<
      ITreatmentDebtItem[],
      { userGuid: string }
    >({
      query: ({ userGuid }) => ({
        url: `Payments/GetAllTreatmentsWithoutPayment/${userGuid}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { userGuid }) => [
        { type: "UserDebt" as const, id: userGuid },
      ],
    }),
    // Get payment types (PaymentsTypes table)
    getPaymentTypes: build.query<IPaymentType[], void>({
      query: () => ({
        url: `Admintech/Admin_PaymentTypes_DML/1`,
        method: "POST",
        body: {},
      }),
      transformResponse: (
        response: Array<{
          Id?: number;
          Name?: string;
          StatusId?: number;
          id?: number;
          name?: string;
          statusId?: number;
        }>
      ) =>
        (response || [])
          .filter((pt) => (pt.statusId ?? pt.StatusId ?? 0) !== 0)
          .map((pt) => ({
            id: pt.id ?? pt.Id ?? 0,
            name: pt.name ?? pt.Name ?? "",
            statusId: pt.statusId ?? pt.StatusId ?? 1,
          })),
    }),
    // Charge user via token (credit card) or create document (cash/check/bank transfer)
    chargeToken: build.mutation<ICreatePaymentResponse, ICreatePaymentRequest>({
      query: (body) => ({
        url: `Payments/ChargeToken`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error) => [
        { type: "admin" as const, id: "payments" },
      ],
    }),
    // Get payment collection methods
    getPaymentCollectionMethods: build.mutation<PaymentCollectionMethodsResponse, void>({
      query: () => ({
        url: `Admintech/Admin_PaymentCollectionMethods_DML/1`,
        method: "POST",
        body: {},
      }),
    }),
    // Table Permissions Management
    getAppTables: build.mutation<IAppTable[], void>({
      query: () => ({
        url: `Admintech/Admin_AppTables_DML/1`,
        method: "POST",
        body: { Id: 0, Name: "SELECT", StatusId: 1 },
      }),
      transformResponse: (response: Array<{ Id?: number; Name?: string; StatusId?: number; id?: number; name?: string; statusId?: number }>) =>
        (response || []).map((table) => ({
          id: table.id ?? table.Id ?? 0,
          name: table.name ?? table.Name ?? "",
          statusId: table.statusId ?? table.StatusId ?? 1,
        })),
    }),
    getTablePermissions: build.mutation<ITablePermission[], void>({
      query: () => ({
        url: `Admintech/Admin_TablePermissions_DML/1`,
        method: "POST",
        body: { Id: 0, Name: "SELECT" },
      }),
      transformResponse: (response: Array<{ Id?: number; Name?: string; StatusId?: number; id?: number; name?: string; statusId?: number }>) =>
        (response || []).map((perm) => ({
          id: perm.id ?? perm.Id ?? 0,
          name: perm.name ?? perm.Name ?? "",
          statusId: perm.statusId ?? perm.StatusId ?? 1,
        })),
    }),
    getUserAppTableRules: build.mutation<IUserAppTableRule[], void>({
      query: () => ({
        url: `Admintech/Admin_UserAppTableRules_DML/1`,
        method: "POST",
        body: { Id: 0, UsersId: null, AppTableId: 0, PermissionId: 0, StatusId: 1 },
      }),
      transformResponse: (response: Array<{
        Id?: number;
        UsersId?: number | null;
        UserName?: string;
        AppTableId?: number;
        TableName?: string;
        PermissionId?: number;
        PermissionName?: string;
        ScopeTypeId?: number;
        ScopeName?: string;
        StatusId?: number;
        CompanyId?: number;
        id?: number;
        usersId?: number | null;
        userName?: string;
        appTableId?: number;
        tableName?: string;
        permissionId?: number;
        permissionName?: string;
        scopeTypeId?: number;
        scopeName?: string;
        statusId?: number;
        companyId?: number;
      }>) =>
        (response || []).map((rule) => ({
          id: rule.id ?? rule.Id ?? 0,
          usersId: rule.usersId ?? rule.UsersId ?? null,
          userName: rule.userName ?? rule.UserName,
          appTableId: rule.appTableId ?? rule.AppTableId ?? 0,
          tableName: rule.tableName ?? rule.TableName,
          permissionId: rule.permissionId ?? rule.PermissionId ?? 0,
          permissionName: rule.permissionName ?? rule.PermissionName,
          scopeTypeId: rule.scopeTypeId ?? rule.ScopeTypeId ?? 2,
          scopeName: rule.scopeName ?? rule.ScopeName,
          statusId: rule.statusId ?? rule.StatusId ?? 1,
          companyId: rule.companyId ?? rule.CompanyId,
        })),
    }),
    createOrUpdateUserAppTableRule: build.mutation<IUserAppTableRule[], Partial<IUserAppTableRule>>({
      query: (body) => ({
        url: `Admintech/Admin_UserAppTableRules_DML/2`,
        method: "POST",
        body: {
          Id: body.id ?? 0,
          UsersId: body.usersId ?? null,
          AppTableId: body.appTableId ?? 0,
          PermissionId: body.permissionId ?? 0,
          ScopeTypeId: body.scopeTypeId ?? 2,
          StatusId: body.statusId ?? 1,
        },
      }),
    }),
    deleteUserAppTableRule: build.mutation<IUserAppTableRule[], { id: number }>({
      query: ({ id }) => ({
        url: `Admintech/Admin_UserAppTableRules_DML/3`,
        method: "POST",
        body: { Id: id, StatusId: 0 },
      }),
    }),
    // SMS Questionnaire Notifications Management
    getSmsQuestionnaireNotifications: build.mutation<ICompanyUserEnterQuestionnaire[], { companyId: number }>({
      query: ({ companyId }) => ({
        url: `Admintech/Admin_CompanyUserEnterQuestionnaire_DML/1/${companyId}`,
        method: "POST",
        body: { Id: 0, QuestionnaireId: 0, CompanyId: companyId, StatusId: 1 },
      }),
      transformResponse: (response: Array<{
        Id?: number;
        QuestionnaireId?: number;
        QuestionnaireName?: string;
        CompanyId?: number;
        FakeCompanyId?: number | null;
        Desc?: string | null;
        Name?: string | null;
        Phone?: string | null;
        Email?: string | null;
        StatusId?: number;
        id?: number;
        questionnaireId?: number;
        questionnaireName?: string;
        companyId?: number;
        fakeCompanyId?: number | null;
        desc?: string | null;
        name?: string | null;
        phone?: string | null;
        email?: string | null;
        statusId?: number;
      }>) =>
        (response || []).map((item) => ({
          id: item.id ?? item.Id ?? 0,
          questionnaireId: item.questionnaireId ?? item.QuestionnaireId ?? 0,
          questionnaireName: item.questionnaireName ?? item.QuestionnaireName,
          companyId: item.companyId ?? item.CompanyId ?? 0,
          fakeCompanyId: item.fakeCompanyId ?? item.FakeCompanyId ?? null,
          desc: item.desc ?? item.Desc ?? null,
          name: item.name ?? item.Name ?? null,
          phone: item.phone ?? item.Phone ?? null,
          email: item.email ?? item.Email ?? null,
          statusId: item.statusId ?? item.StatusId ?? 1,
        })),
    }),
    createOrUpdateSmsQuestionnaireNotification: build.mutation<ICompanyUserEnterQuestionnaire[], { body: Partial<ICompanyUserEnterQuestionnaire>; companyId: number }>({
      query: ({ body, companyId }) => ({
        url: `Admintech/Admin_CompanyUserEnterQuestionnaire_DML/2/${companyId}`,
        method: "POST",
        body: {
          Id: body.id ?? 0,
          QuestionnaireId: body.questionnaireId ?? 0,
          CompanyId: body.companyId ?? companyId,
          FakeCompanyId: body.fakeCompanyId ?? null,
          Desc: body.desc ?? null,
          Name: body.name ?? null,
          Phone: body.phone ?? null,
          Email: body.email ?? null,
          StatusId: body.statusId ?? 1,
        },
      }),
    }),
    deleteSmsQuestionnaireNotification: build.mutation<ICompanyUserEnterQuestionnaire[], { id: number; companyId: number }>({
      query: ({ id, companyId }) => ({
        url: `Admintech/Admin_CompanyUserEnterQuestionnaire_DML/3/${companyId}`,
        method: "POST",
        body: { Id: id, StatusId: 0 },
      }),
    }),
    getQuestionnaireCompaniesSpecificTypes: build.query<IQuestionnaireCompaniesSpecificTypes[], void>({
      query: () => ({
        url: `Admintech/Admin_QuestionnaireCompaniesSpecificTypes`,
        method: "GET",
      }),
    }),
    getSMSDelivery: build.mutation<TSMSDeliveryType[], number>({
      query: (companyId) => ({
        url: `Companies/CompanySMSDelivery_DML/1/${companyId}`,
        method: "POST",   // matches current backend — change to GET once fixed
        body: [],         // backend expects body even for Type=1
      }),
    }),
    updateSMSDelivery: build.mutation<TSMSDeliveryType[], { companyId: number; items: TUpsertItem[] }>({
      query: ({ companyId, items }) => ({
        url: `Companies/CompanySMSDelivery_DML/2/${companyId}`,
        method: "POST",
        body: items,
      }),
    }),
    // Secure Document Delivery — send payment history PDF link
    sendPaymentHistoryLink: build.mutation<
      { success: boolean; tokenId: string },
      { clientId: number; companyId: number; deliveryChannel: number; fromDate?: string; toDate?: string }
    >({
      query: (body) => ({
        url: `Payments/SendPaymentHistoryLink`,
        method: "POST",
        body,
      }),
    }),
    sendInvoiceBundleLink: build.mutation<
      { success: boolean; tokenId: string },
      { clientId: number; companyId: number; deliveryChannel: number }
    >({
      query: (body) => ({
        url: `Payments/SendInvoiceBundleLink`,
        method: "POST",
        body,
      }),
    }),
    // Bulk send payment history PDF links to multiple clients
    sendPaymentHistoryLinkBulk: build.mutation<
      {
        results: { clientId: number; success: boolean; error?: string }[];
        totalSent: number;
        totalFailed: number;
      },
      { clientIds: number[]; companyId: number; deliveryChannel: number; fromDate?: string; toDate?: string }
    >({
      query: (body) => ({
        url: `Payments/SendPaymentHistoryLinkBulk`,
        method: "POST",
        body,
      }),
    }),
    getStatuses: build.query<TGetStatusesResponse, void>({
      query: () => ({
        url: `/General/Gen_GetTableByTableName/statusrequest`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetSupportStatusesQuery,
  useGetMigzarsQuery,
  useGetGendersQuery,
  useGetSupportMessagesMutation,
  usePostSupportMessageMutation,
  useCreateSupportTicketMutation,
  usePostSupportMessageCommentMutation,
  usePostFileMutation,
  useGetCompaniesMutation,
  useAddOrEditCompanyMutation,
  useDeleteCompanyMutation,
  useRestoreCompanyMutation,
  useGetQuestionnairesMutation,
  useDeleteQuestionnaireMutation,
  useDeleteQuestionsMutation,
  useDeleteMultipleQuestionsMutation,
  useDuplicateQuestionMutation,
  useAddOrEditQuestionnaireMutation,
  useAddOrEditQuestionsMutation,
  useGetQuestionsMutation,
  useGetQuestionsOptionsMutation,
  useAddOrEditQuestionsOptionsMutation,
  useDeleteQuestionsOptionsMutation,
  useGetQuestionsGroupsMutation,
  useAddOrEditQuestionsGroupsMutation,
  useDeleteQuestionsGroupsMutation,
  useGetCompanyGroupsRRMHeadersMutation,
  useAddOrEditCompanyGroupsRRMHeadersMutation,
  useDeleteCompanyGroupsRRMHeadersMutation,
  useGetCompanyGroupsRRMMutation,
  useAddOrEditCompanyGroupsRRMMutation,
  useGetCompanyGeneralRRMMutation,
  useAddOrEditCompanyGeneralRRMMutation,
  useDeleteCompanyGeneralRRMMutation,
  useGetAdminMessagesMutation,
  useAddOrEditAdminMessageMutation,
  useDeleteAdminMessageMutation,
  useBulkCreateAdminMessagesMutation,
  useGetExercisesMutation,
  useAddOrEditExercisesMutation,
  useDeleteExercisesMutation,
  useGetExercisesCategoriesMutation,
  useAddOrEditExercisesCategoriesMutation,
  // useGetQuestionnaireCompaniesHeadersMutation,
  useAddOrEditQuestionnaireCompaniesHeadersMutation,
  useDeleteQuestionnaireCompaniesHeadersMutation,
  useGetQuestionnaireCompaniesMutation,
  useAddOrEditQuestionnaireCompaniesMutation,
  useDeleteQuestionnaireCompaniesMutation,
  useLazyGetAllMetaTablesQuery,
  useGetAppointmentsProgramsByCompanyIdQuery,
  useGetAllMetaTablesQuery,
  useGetAppointmentsByCompanyAndMonthQuery,
  useLazyGetAppointmentsByCompanyAndMonthQuery,
  useGetAppointmentsProgramsByUserIdMutation,
  useGetAppointmentsProgramsByTherapistsIdMutation,
  usePostAppointmentsProgramsMutation,
  useGetAppointmentsProgramsTherapistsMutation,
  useGetAppointmentsProgramsUnavailableTherapistsMutation,
  useDeleteAppointmentsProgramsMutation,
  useGetAppointmentsProgramsScheduleMutation,
  useGetCompanyAppointmentsProgramsMutation,
  useDeleteAppointmentMutation,
  useDeleteAppointmentsProgramWithAllAppointmentsMutation,
  useLazyGetAllCombosForSearchUsersQuery,
  useLazyConfirmAccessToUsersQuery,
  useGetAllOptionsDataTypesQuery,
  useGetAllQuestionsSpecificTypesQuery,
  useGetAllQuestionsUsersFieldsQuery,
  useGetSpecialQuestionsDisplayQuery,
  useGetAllCitiesQuery,
  useUpdateTherapistTakingTreatmentPlanMutation,
  useUpdateUnavailableTherapistTakingTreatmentPlanMutation,
  usePostTreatmentPlanAutoAssignMutation,
  useLazyGetDiagramsTypesQuery,
  useGetAllSmsMessagesMutation,
  usePostSmsMessageMutation,
  useDeleteSmsMessageMutation,
  useGetRoomsFeaturesQuery,
  useGetTherapyTypesQuery,
  useGetAllPlansQuery,
  usePostPlanMutation,
  useDeletePlanMutation,
  useGetPlanPricesQuery,
  usePostPlanPricesMutation,
  useDeletePlanPriceMutation,
  useGetLinkForQuestionnaireQuery,
  useLazyGetLinkForQuestionnaireQuery,
  useGetQuestionnaireCompaniesTypesByCompanyIdMutation,
  usePostQuestionnaireCompaniesTypesMutation,
  useCopyQuestionnaireWithQuestionsAndOptionsMutation,
  useLazyGetClinicalUserSummaryQuery,
  useGetClinicalUserSummaryQuery,
  useGetQuestionnaireCompaniesHeadersQuery,
  usePostCommentsToAppointmentsProgramsMutation,
  useGetAppointmentsProgramsCommentsQuery,
  usePostVideoMutation,
  useUploadCompanyLogoMutation,
  useGetAllFeaturesQuery,
  usePostFeatureMutation,
  useDeleteFeatureMutation,
  //useGetAllFeatures2PlanQuery,
  useLazyGetAllFeatures2PlanQuery,
  usePostFeatures2PlanMutation,
  useDeleteFeatures2PlanMutation,
  useGetAllCurrenciesQuery,
  useGetProtocolsByCompanyQuery,
  useGetProtocolComponentsQuery,
  useGetPaymentsByCompanyIdQuery,
  useGetCompanyPaymentsSummaryQuery,
  useLazyGetCompanyPaymentsSummaryQuery,
  useGetPaymentsByUserIdQuery,
  useGetUserBalanceHistoryQuery,
  useGetCardLast4DigitsQuery,
  useTogglePaymentStatusMutation,
  useTransactionRefundMutation,
  useBulkTransactionsRefundMutation,
  useCreatePaymentPageMutation,
  useCreateBulkPaymentJobMutation,
  useGetJobsQuery,
  useGetJobQuery,
  useGetJobItemsQuery,
  useGetJobItemAttemptsQuery,
  useRetryJobItemMutation,
  useRetryJobFailedItemsMutation,
  useCancelSubscriptionMutation,
  useGetTreatmentRoomsQuery,
  useAddOrEditTreatmentRoomMutation,
  useDeleteTreatmentRoomMutation,
  useGetTreatmentGroupsQuery,
  useGetTreatmentGroupByIdQuery,
  useCreateTreatmentGroupMutation,
  useUpdateTreatmentGroupMutation,
  useDeleteTreatmentGroupMutation,
  useAddGroupMemberMutation,
  useRemoveGroupMemberMutation,
  useGetGroupMembersQuery,
  useCreateGroupTreatmentPlanMutation,
  useGetGroupTreatmentPlansQuery,
  useLazyGetGroupTreatmentPlansQuery,
  useGetRootCompaniesQuery,
  useGetCompanyChargeTriggersQuery,
  useGetCompanyFundingSourcesQuery,
  useAddCompanyFundingSourceMutation,
  useRemoveCompanyFundingSourceMutation,
  useChangeSubscriptionPlanMutation,
  useChangeSubscriptionCardMutation,
  // Company Services & Prices
  useGetCompanyServicesQuery,
  useCreateOrUpdateCompanyServiceMutation,
  useDeleteCompanyServiceMutation,
  useGetCompanyServicesPricesQuery,
  useCreateOrUpdateCompanyServicePriceMutation,
  useDeleteCompanyServicePriceMutation,
  useGetCompanyByIdQuery,
  // Payment Clearing Configuration (Super Admin only)
  useGetPaymentClearingCompaniesQuery,
  useGetCompanyPaymentClearingConfigurationQuery,
  useSaveCompanyPaymentClearingConfigurationMutation,
  useGetPaymentLinkSettingsQuery,
  useSavePaymentLinkSettingsMutation,
  useGetPaymentLinkSessionsQuery,
  // User Debt / Treatments Without Payment
  useGetTreatmentsWithoutPaymentQuery,
  useLazyGetTreatmentsWithoutPaymentQuery,
  // Payment Types
  useGetPaymentTypesQuery,
  // Charge Token (direct charge or document creation)
  useChargeTokenMutation,
  // Cancel Appointment
  useGetCancelAppReasonsQuery,
  useLazyGetCancelAppReasonsQuery,
  useCancelAppointmentMutation,
  // Edit Appointment
  useValidateAppointmentChangeMutation,
  usePostAppointmentMutation,
  useCreateAppointmentFastMutation,
  usePatchAppointmentMutation,
  // Series management
  useGetSeriesAppointmentsQuery,
  useLazyGetSeriesAppointmentsQuery,
  useExtendSeriesMutation,
  // Program session management (series cubes, program-driven)
  useGetProgramSessionsQuery,
  useAddProgramSessionsMutation,
  useDeleteProgramSessionMutation,
  useLazyGetProgramConfigQuery,
  useConvertToSeriesMutation,
  // Payment Collection Methods
  useGetPaymentCollectionMethodsMutation,
  // Table Permissions
  useGetAppTablesMutation,
  useGetTablePermissionsMutation,
  useGetUserAppTableRulesMutation,
  useCreateOrUpdateUserAppTableRuleMutation,
  useDeleteUserAppTableRuleMutation,
  // SMS Questionnaire Notifications
  useGetSmsQuestionnaireNotificationsMutation,
  useCreateOrUpdateSmsQuestionnaireNotificationMutation,
  useDeleteSmsQuestionnaireNotificationMutation,
  useGetPatientDefaultServicePricesQuery,
  useCreatePatientDefaultServicePricesMutation,
  useDeletePatientDefaultServicePriceMutation,
  useChangeTherapistsCanSeePricesMutation,
  useChangeCanAddDefaultReportsMutation,
  useChangeAdminCanViewPatientRecordingsMutation,
  useGetQuestionnaireCompaniesSpecificTypesQuery,
  // Audio Retention
  useSetAudioRetentionMutation,
  useGetAudioRetentionQuery,
  useGetSMSDeliveryMutation,
  useUpdateSMSDeliveryMutation,
  // Secure Document Delivery
  useSendPaymentHistoryLinkMutation,
  useSendInvoiceBundleLinkMutation,
  useSendPaymentHistoryLinkBulkMutation,
  useGetStatusesQuery,
} = utilsApi;
